(() => {
  const instances = new WeakSet();
  let itemSequence = 0;
  let threeModulesPromise;
  const designerBuildVersion = 'R48-B9-20261007-r1';
  // Image retries: a failed image is retried with back-off, and again when the
  // phone comes back online or the page becomes visible, instead of staying blank.
  const imageRetryDelays = [800, 2000, 4000, 8000];
  const failedImages = new Set();
  function withRetryParam(source, attempt) {
    const separator = String(source).includes('?') ? '&' : '?';
    return `${source}${separator}retry=${attempt}`;
  }
  function attachImageRetry(image, onFinalFailure) {
    image.dataset.retryAttempt = '0';
    image.addEventListener('error', () => {
      if (!image.dataset.catalogSrc) { onFinalFailure?.(); return; }
      const attempt = Number(image.dataset.retryAttempt || 0);
      if (attempt >= imageRetryDelays.length) {
        failedImages.add(image);
        onFinalFailure?.();
        return;
      }
      image.dataset.retryAttempt = String(attempt + 1);
      window.setTimeout(() => {
        if (image.isConnected) image.src = withRetryParam(image.dataset.catalogSrc, attempt + 1);
      }, imageRetryDelays[attempt]);
    });
  }
  function retryFailedImages() {
    [...failedImages].forEach((image) => {
      failedImages.delete(image);
      if (!image.isConnected || !image.dataset.catalogSrc) return;
      image.dataset.retryAttempt = '0';
      image.src = withRetryParam(image.dataset.catalogSrc, Date.now());
    });
  }
  window.addEventListener('online', retryFailedImages);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') retryFailedImages(); });
  // The colour used when a material photo cannot load. Matches the material's own
  // name, so a clear bead is never drawn gold.
  function colorForMaterialName(name) {
    const text = String(name || '').toLowerCase();
    const rules = [
      [/red|ruby|garnet|coral|agate|strawberry/, '#a33a3a'],
      [/pink|rose|peach|cherry|blush/, '#dca3ac'],
      [/purple|amethyst|violet|lilac|lavender/, '#735287'],
      [/blue|lapis|sodalite|kyanite|turquoise/, '#3d6a8f'],
      [/green|jade|aventurine|malachite|peridot/, '#5f8a63'],
      [/yellow|citrine|amber|honey/, '#d9a846'],
      [/tiger|\beye/, '#9a6425'],
      [/black|onyx|obsidian|hematite/, '#2b2f36'],
      [/gold/, '#c69a40'],
      [/silver/, '#adb5ba'],
      [/clear|white|quartz|crystal|moonstone|pearl|elastic/, '#dce5e7']
    ];
    const match = rules.find(([pattern]) => pattern.test(text));
    return match ? match[1] : '#c9c4bb';
  }

  const number = (value) => Number.parseFloat(value || '0') || 0;
  const parsedNumber = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const positiveNumber = (...values) => values
    .map(parsedNumber)
    .find((value) => value !== null && value > 0) ?? null;
  // fitWidth is the catalog's legacy calculated field.  Some pendant rows
  // intentionally keep it at 1 for commerce compatibility, while their
  // physical along-string occupation is their visible width plus the hook.
  // Keep the raw field for catalog reconciliation and use this single
  // physical function for every customer-facing measurement and layout.
  const occupiedWidth = (item) => positiveNumber(item?.fitWidth, item?.size, item?.width) || 0;
  const isPendantItem = (item) => item?.kind === 'charm' || item?.shape === 'defaultPendant';
  // A charm hangs from the cord, so it takes only its commerce fit width (1 mm,
  // the same value the order records), not a bead's space on the ring.
  const physicalOccupation = (item) => occupiedWidth(item);
  // Charms stay upright and hang outside the beads. A radial turn looked sideways next to the beads.
  const charmScale = 0.85;
  // Charms swing gently with the ring: tops lean in, bottoms lean out, sides stay upright (radians of swing).
  const charmSwing = 0.5;
  // Room the charms need outside the ring, so the stage and the product picture scale to fit them:
  // the bead rim, then the charm body, then the loop gap.
  const pendantOuterMm = (items) => {
    const beadRimMm = items.reduce((largest, item) => (isPendantItem(item) ? largest : Math.max(largest, number(item.size) * .49)), 0);
    return items.filter(isPendantItem).reduce((best, item) => Math.max(best, (Math.max(number(item.width) || item.size || 8, number(item.height) || item.size || 8) * charmScale + beadRimMm + 1.2) * 2), 0);
  };
  const beadDiameter = (item) => positiveNumber(item?.size, item?.width, item?.fitWidth) || 8;
  const materialHeight = (item) => positiveNumber(item?.height, item?.width, item?.size, item?.fitWidth) || 0;
  const roundedMillimeterSum = (sum, value) => Number((sum + value).toFixed(2));
  const cloneItems = (items) => items.map((item) => ({ ...item }));
  const minimumZoom = 0.4;
  const maximumZoom = 2;
  const pixelsPerMm = 5;
  const minimumOrbitMm = 130;
  const clampZoom = (zoom) => Math.min(maximumZoom, Math.max(minimumZoom, zoom));

  const initDesigner = (root) => {
    if (instances.has(root)) return;
    instances.add(root);
    root.dataset.melDesignerVersion = designerBuildVersion;
    root.dataset.mountMode = root.closest('[data-mel-designer-page]') ? 'page' : 'embedded';
    const siteHeader = document.querySelector('[data-mel-header]');
    const updateChromeHeight = () => root.style.setProperty('--mel-designer-chrome-height', `${siteHeader?.offsetHeight || 0}px`);
    updateChromeHeight();
    if (siteHeader && 'ResizeObserver' in window) new ResizeObserver(updateChromeHeight).observe(siteHeader);
    const allowAnyFit = () => root.dataset.allowAnyFit === 'true';
    // Programmatic focus after a pointer action is not keyboard navigation.
    root.dataset.inputModality = 'pointer';
    root.addEventListener('pointerdown', () => { root.dataset.inputModality = 'pointer'; }, true);
    root.addEventListener('touchstart', () => { root.dataset.inputModality = 'pointer'; }, {capture:true,passive:true});
    document.addEventListener('keydown', event => { if (event.key === 'Tab') root.dataset.inputModality = 'keyboard'; }, true);

    const canvas = root.querySelector('[data-designer-canvas]');
    const layer = root.querySelector('[data-bead-layer]');
    const materialDropSlot = root.querySelector('[data-material-drop-slot]');
    const cord = root.querySelector('[data-designer-cord]');
    const empty = root.querySelector('[data-empty-state]');
    const emptyCopy = root.querySelector('[data-empty-copy]');
    const clearToast = root.querySelector('[data-clear-toast]');
    const clearToastText = root.querySelector('[data-clear-toast-text]');
    const clearToastUndo = root.querySelector('[data-clear-toast-undo]');
    let clearToastTimer = 0;
    const selection = root.querySelector('[data-selection-toolbar]');
    const selectionMore = root.querySelector('[data-selection-more]');
    // Phones show four main actions; "More" opens the rest. Closing is the
    // same button, and the sheet closes when the selection is cleared.
    selectionMore?.addEventListener('click', () => {
      const open = selection?.dataset.more !== 'open';
      if (selection) selection.dataset.more = open ? 'open' : '';
      selectionMore.setAttribute('aria-expanded', String(open));
    });
    const selectedName = root.querySelector('[data-selected-name]');
    const live = root.querySelector('[data-live-region]');
    const sizeButtons = [...root.querySelectorAll('[data-size-option]')];
    const sizePresets = new Map(sizeButtons.map((button) => [button.dataset.size, {
      size: button.dataset.size,
      wristMin: number(button.dataset.wristMin),
      wristMax: number(button.dataset.wristMax),
      targetMm: number(button.dataset.targetMm)
    }]));
    const orderedPresets = [...sizePresets.values()].sort((first, second) => first.targetMm - second.targetMm);
    const customSizeControl = root.querySelector('[data-custom-size-control]');
    const customWristInput = root.querySelector('[data-custom-wrist]');
    const wristUnitButtons = [...root.querySelectorAll('[data-wrist-unit]')];
    const customWristUnitLabel = root.querySelector('[data-custom-wrist-unit]');
    const customWristHint = root.querySelector('[data-custom-size-hint]');
    const customSize = 'CUSTOM';
    const customWristMin = number(customWristInput?.dataset.wristMinMm) || 120;
    const customWristMax = number(customWristInput?.dataset.wristMaxMm) || 240;
    const comfortAllowanceMm = number(customWristInput?.dataset.comfortMm) || 15;
    const preferredEaseMm = 8;
    const priceOutput = root.querySelector('[data-stat-price]');
    const lengthOutput = root.querySelector('[data-stat-length]');
    const countOutput = root.querySelector('[data-stat-count]');
    const progress = root.querySelector('[data-progress-bar]');
    const fitSize = root.querySelector('[data-fit-size]');
    const fitRange = root.querySelector('[data-fit-range]');
    const fitRecommended = root.querySelector('[data-fit-recommended]');
    const fitTarget = root.querySelector('[data-fit-target]');
    const fitRemaining = root.querySelector('[data-fit-remaining]');
    const fitAdviceButton = root.querySelector('[data-fit-advice]');
    const dialog = root.querySelector('[data-preview-dialog]');
    const measureDialog = root.querySelector('[data-measure-dialog]');
    const previewCanvas = root.querySelector('[data-preview-canvas]');
    const previewLayer = root.querySelector('[data-preview-layer]');
    const previewCord = root.querySelector('[data-preview-cord]');
    const previewPrice = root.querySelector('[data-preview-price]');
    const previewName = root.querySelector('[data-preview-name]');
    const previewMeta = root.querySelector('[data-preview-meta]');
    const previewList = root.querySelector('[data-preview-list]');
    const previewPriceDetail = root.querySelector('[data-preview-price-detail]');
    let reviewListExpanded = false;
    const reviewDesignNameInput = root.querySelector('[data-review-design-name]');
    const reviewContent = root.querySelector('.mel-designer__review-content');
    const purchaseBar = root.querySelector('[data-purchase-bar]');
    const reservePurchaseSpace = () => {
      const height = (purchaseBar?.getBoundingClientRect().height || 0) + (root.querySelector('.mel-designer__secure-note')?.getBoundingClientRect().height || 0);
      reviewContent?.style.setProperty('--purchase-zone-height', `${Math.ceil(height)}px`);
    };
    if (purchaseBar && 'ResizeObserver' in window) new ResizeObserver(reservePurchaseSpace).observe(purchaseBar);
    const mobileReviewPrice = root.querySelector('[data-mobile-review-price]');
    const fitCheckoutNote = root.querySelector('[data-fit-checkout-note]');
    const cartLink = root.querySelector('[data-designer-cart-link]');
    const designNameInput = root.querySelector('[data-design-name]');
    const zoomValue = root.querySelector('[data-zoom-value]');
    const wristHost = root.querySelector('[data-wrist-three]');
    const wristToneButtons = [...root.querySelectorAll('[data-wrist-tone]')];
    const wristViewButtons = [...root.querySelectorAll('[data-wrist-view]')];
    const wristFramingButton = root.querySelector('[data-wrist-framing]');
    const previewViewButtons = [...root.querySelectorAll('[data-preview-view]')];
    const previewPanels = [...root.querySelectorAll('[data-preview-panel]')];
    const wristComingNote = root.querySelector('[data-wrist-coming-note]');
    const wristFallback = root.querySelector('[data-wrist-fallback]');
    const wristFallbackCanvas = root.querySelector('[data-wrist-fallback-canvas]');
    const wristFallbackLayer = root.querySelector('[data-wrist-fallback-layer]');
    const wristFallbackCord = root.querySelector('[data-wrist-fallback-cord]');
    const wristFallbackNote = root.querySelector('[data-wrist-fallback-note]');
    const editorViewButtons = [...root.querySelectorAll('[data-editor-view]')];
    const commerceStatus = root.querySelector('[data-commerce-status]');
    const shareStatus = root.querySelector('[data-share-status]');
    const cardRulerStatus = root.querySelector('[data-card-ruler-status]');
    const cardCalibrateButton = root.querySelector('[data-card-calibrate]');
    const materialDetailCopy = root.querySelector('[data-material-detail-copy]');
    const actionButtons = [...root.querySelectorAll('[data-designer-action]')];
    let materialButtons = [...root.querySelectorAll('[data-material]')];
    const materialSearch = root.querySelector('[data-material-search]');
    const materialEmpty = root.querySelector('[data-material-empty]');
    const materialTypes = root.querySelector('[data-material-types]');
    const catalogSummary = root.querySelector('[data-catalog-summary]');
    const catalogNotice = root.querySelector('[data-catalog-notice]');
    const catalogNoticeText = root.querySelector('[data-catalog-notice-text]');
    const catalogRetryButtons = [...root.querySelectorAll('[data-catalog-retry]')];
    const catalogReconcile = root.querySelector('[data-catalog-reconcile]');
    const catalogReconcileText = root.querySelector('[data-catalog-reconcile-text]');
    const catalogReconcileReplace = root.querySelector('[data-catalog-reconcile-replace]');
    const catalogReconcileRemove = root.querySelector('[data-catalog-reconcile-remove]');
    const catalogUpdateToast = root.querySelector('[data-catalog-update-toast]');
    const commerceCatalogNote = root.querySelector('[data-commerce-catalog-note]');
    const commerceCatalogText = root.querySelector('[data-commerce-catalog-text]');
    const catalogPanels = new Map(
      [...root.querySelectorAll('[data-catalog-panel]')]
        .map((panel) => [panel.dataset.catalogPanel, panel])
    );
    const tabList = root.querySelector('.mel-designer__tabs');
    const tabs = [...root.querySelectorAll('[data-category-tab]')];
    const panels = [...root.querySelectorAll('[data-category-panel]')];
    const currency = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: root.dataset.currencyCode || 'USD'
    });
    const storageKey = 'mel-bracelet-designer-draft-v1';
    const catalogStorageKey = 'mel-bracelet-designer-catalog-v1';
    const savedStatus = root.querySelector('[data-draft-status]');
    let draftTimer = 0;
    let lastDraftJSON = '';
    let draftInitialized = false;
    const canvasImageCache = new Map();
    const canvasImageFailures = new Map();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const coarsePointer = window.matchMedia('(pointer: coarse)');
    const threeInteractionNote = () => coarsePointer.matches
      ? 'Drag to rotate · Pinch to zoom'
      : 'Drag to rotate · Ctrl + scroll to zoom';
    const pendingFlightIds = new Set();
    const pendingArrivalOrigins = new Map();
    const beadPositions = new WeakMap();
    const materialUsageCache = new WeakMap();
    const materialFeedbackTimers = new WeakMap();
    let arrivalFrame = 0;
    let renderFrame = 0;
    let zoomFrame = 0;
    let zoomEndTimer = 0;
    const messages = {
      added: root.dataset.messageAdded,
      removed: root.dataset.messageRemoved,
      moved: root.dataset.messageMoved,
      saved: root.dataset.messageSaved,
      draftStorageError: root.dataset.messageDraftStorageError,
      restored: root.dataset.messageRestored,
      full: root.dataset.messageFull,
      maxSize: root.dataset.messageMaxSize,
      checking: root.dataset.messageChecking,
      creating: root.dataset.messageCreating,
      carting: root.dataset.messageCarting,
      cartAdded: root.dataset.messageCartAdded,
      cartUnknown: root.dataset.messageCartUnknown,
      cartRecoveryConflict: root.dataset.messageCartRecoveryConflict,
      priceChanged: root.dataset.messagePriceChanged,
      commerceError: root.dataset.messageCommerceError,
      serviceUnavailable: root.dataset.messageServiceUnavailable,
      inspired: root.dataset.messageInspired,
      catalogLoading: root.dataset.messageCatalogLoading,
      catalogLoaded: root.dataset.messageCatalogLoaded,
      catalogError: root.dataset.messageCatalogError,
      catalogStale: root.dataset.messageCatalogStale,
      catalogUpdatedInline: root.dataset.messageCatalogUpdatedInline,
      catalogUnavailable: root.dataset.messageCatalogUnavailable,
      catalogReplace: root.dataset.messageCatalogReplace,
      catalogRemoveUnavailable: root.dataset.messageCatalogRemoveUnavailable,
      fitCheckoutBlocked: root.dataset.messageFitCheckoutBlocked,
      catalogCheckoutLoading: root.dataset.messageCatalogCheckoutLoading
    };
    const labels = {
      wrist: root.dataset.labelWrist,
      recommended: root.dataset.labelRecommended,
      remaining: root.dataset.labelRemaining,
      over: root.dataset.labelOver,
      target: root.dataset.labelTarget,
      custom: root.dataset.labelCustom,
      designFit: root.dataset.labelDesignFit,
      addBeads: root.dataset.labelAddBeads,
      unmeasurable: root.dataset.labelUnmeasurable,
      short: root.dataset.labelShort,
      long: root.dataset.labelLong,
      fitOrderMinimum: root.dataset.labelFitOrderMinimum,
      fitOrderMaximum: root.dataset.labelFitOrderMaximum,
      tooTight: root.dataset.labelTooTight,
      snug: root.dataset.labelSnug,
      good: root.dataset.labelGood,
      loose: root.dataset.labelLoose,
      tooLoose: root.dataset.labelTooLoose,
      allMaterials: root.dataset.labelAllMaterials,
      inUse: root.dataset.labelInUse,
      used: root.dataset.labelUsed,
      materialPhoto: root.dataset.labelMaterialPhoto,
      dragMaterial: root.dataset.labelDragMaterial
    };
    const defaultSize = sizeButtons.find((button) => button.getAttribute('aria-pressed') === 'true')?.dataset.size || 'M';
    let activeWristView = 'top', activeWristTone = 'light', activeWristFraming = 'wrist';
    let visibleWristRenderRevision = 0, wristRenderChain = Promise.resolve(), wristThreeView;
    let wristPhotosPromise;
    const loadThreeModules = () => threeModulesPromise ||= Promise.all([import(root.dataset.threeUrl), import(root.dataset.rendererUrl)]).catch(error => { threeModulesPromise = null; throw error; });
    const loadWristPhotos = () => wristPhotosPromise ||= (async () => {
      if (root.dataset.photoApproved !== 'true') throw new Error('Wrist photographs are awaiting approval');
      const response = await fetch(root.dataset.wristCalibrationUrl, { signal:AbortSignal.timeout(8000) });
      if (!response.ok) throw new Error('Wrist calibration unavailable');
      const entries = await response.json();
      const urls = JSON.parse(root.querySelector('[data-wrist-photo-urls]')?.textContent || '{}');
      if (entries.length !== 6 || entries.some(photo => photo.approval !== 'APPROVED' || !urls[photo.id])) throw new Error('Wrist photographs are not ready');
      return entries.map(photo => ({ ...photo, image:urls[photo.id] }));
    })().catch(error => { wristPhotosPromise = null; throw error; });
    let catalogItems = [];
    let catalogTypes = [];
    let activeMaterialType = 'all';
    let catalogReady = false;
    let catalogLoadFailed = false;
    let usingStoredCatalog = false;
    let catalogRetryCount = 0;
    let catalogFailureReason = '';
    let catalogRendering = false;
    let catalogReconciliation = { updated: [], unavailable: [] };
    let catalogUpdateTimer = 0;
    let replacingId = null;
    let replacingAllKey = null;
    const library = root.querySelector('.mel-designer__library');
    const panelHandle = root.querySelector('[data-panel-handle]');
    const updateStudioLayout = () => {
      root.dataset.studioLayout = window.innerWidth < 1024 && window.innerHeight >= window.innerWidth ? 'compact' : 'wide';
    };
    updateStudioLayout();
    window.addEventListener('resize', updateStudioLayout, { passive: true });
    let activeMaterialDragButton = null;
    let materialDragGhost = null;
    let materialDropIndex = null;
    let suppressMaterialClickUntil = 0;
    const materialDragMedia = window.matchMedia('(min-width: 750px)');
    const isCompactLayout = () => root.dataset.studioLayout === 'compact';
    const commerceAssetEndpoint = String(root.dataset.commerceAssetsEndpoint || '').trim();
    const boundMaterialButtons = new WeakSet();
    const catalogImageObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const image = entry.target;
          const source = image.dataset.catalogSrc;
          if (source) image.src = source;
          observer.unobserve(image);
        });
      }, { rootMargin: '48px 64px' })
      : null;
    const state = {
      items: [],
      size: defaultSize,
      customWristMm: number(customWristInput?.value) || 172,
      customAutoFit: false,
      cord: { key: 'clear-cord', name: '', visual: 'clear', price: 0 },
      selectedId: null,
      designName: designNameInput?.value || 'Untitled design',
      rotationRad: 0,
      zoom: 1,
      history: [],
      requestId: '',
      commerceBusy: false
    };
    const wristUnits = {
      mm: { scale: 1, decimals: 0, step: '1' },
      cm: { scale: 10, decimals: 1, step: '0.1' },
      in: { scale: 25.4, decimals: 2, step: '0.01' }
    };
    let wristDisplayUnit = 'mm';
    const displayWrist = (millimeters) => (millimeters / wristUnits[wristDisplayUnit].scale)
      .toFixed(wristUnits[wristDisplayUnit].decimals);
    const enteredWristMm = () => {
      const entered = Number.parseFloat(customWristInput?.value || '');
      return Number.isFinite(entered) ? Math.round(entered * wristUnits[wristDisplayUnit].scale) : null;
    };
    const syncWristUnit = (forceValue = false) => {
      if (!customWristInput) return;
      customWristInput.min = displayWrist(customWristMin);
      customWristInput.max = displayWrist(customWristMax);
      customWristInput.step = wristUnits[wristDisplayUnit].step;
      if (forceValue || document.activeElement !== customWristInput) {
        customWristInput.value = displayWrist(state.customWristMm);
      }
      customWristInput.setAttribute('aria-label', customWristInput.dataset[`unitLabel${wristDisplayUnit === 'in' ? 'In' : wristDisplayUnit[0].toUpperCase() + wristDisplayUnit.slice(1)}`] || '');
      if (customWristHint) customWristHint.textContent = customWristInput.dataset[`unitHint${wristDisplayUnit === 'in' ? 'In' : wristDisplayUnit[0].toUpperCase() + wristDisplayUnit.slice(1)}`] || '';
      if (customWristUnitLabel) customWristUnitLabel.textContent = wristDisplayUnit;
      wristUnitButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.wristUnit === wristDisplayUnit)));
    };

    const announce = (message, itemName = '') => {
      live.textContent = itemName ? `${message}: ${itemName}` : message;
    };
    const hideClearToast = () => {
      window.clearTimeout(clearToastTimer);
      clearToastTimer = 0;
      if (clearToast) clearToast.hidden = true;
    };
    const showClearToast = () => {
      if (!clearToast) return;
      clearToastText && (clearToastText.textContent = 'Design cleared ·');
      clearToast.hidden = false;
      window.clearTimeout(clearToastTimer);
      clearToastTimer = window.setTimeout(hideClearToast, 6000);
    };
    const syncInteractionCopy = () => {
      if (emptyCopy) emptyCopy.textContent = coarsePointer.matches ? 'Tap a bead below to start' : 'Choose a bead on the left to start';
    };
    syncInteractionCopy();
    coarsePointer.addEventListener?.('change', syncInteractionCopy);

    const snapshot = () => ({
      items: cloneItems(state.items),
      size: state.size,
      customWristMm: state.customWristMm,
      customAutoFit: state.customAutoFit,
      cord: { ...state.cord },
      selectedId: state.selectedId,
      designName: state.designName,
      rotationRad: state.rotationRad,
      zoom: state.zoom,
      zoomMode: 'relative-fit',
      requestId: state.requestId
    });

    // Share links carry only catalog keys and fit settings.  Images, prices and
    // stock are always resolved again from the current signed material feed.
    const shareEncode = (value) => {
      const bytes = new TextEncoder().encode(value);
      let binary = '';
      bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
      return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
    };
    const shareDecode = (value) => {
      const padded = String(value || '').replace(/-/g, '+').replace(/_/g, '/') + '==='.slice((String(value || '').length + 3) % 4);
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
      return new TextDecoder().decode(bytes);
    };
    const sharePayload = () => ({
      v: 1,
      n: String(state.designName || '').slice(0, 48),
      s: state.size,
      w: state.customWristMm,
      c: state.cord.key,
      i: state.items.map((item) => item.key).filter(Boolean).slice(0, 40)
    });
    const sharedTokenFromLocation = () => {
      const match = String(window.location.hash || '').match(/(?:^#|&)design=([^&]+)/);
      return match?.[1] || '';
    };
    let sharedDesignToken = sharedTokenFromLocation();
    const applySharedDesign = () => {
      if (!sharedDesignToken || !catalogReady) return false;
      try {
        const payload = JSON.parse(shareDecode(sharedDesignToken));
        if (payload?.v !== 1 || !Array.isArray(payload.i) || payload.i.length > 40) return false;
        const byKey = new Map(catalogItems.map((material) => [material.key, material]));
        const materials = payload.i.map((key) => byKey.get(String(key))).filter(Boolean);
        if (!materials.length || materials.length !== payload.i.length) return false;
        remember();
        state.items = materials.map((material) => itemFromMaterial(material));
        state.size = payload.s === customSize || sizePresets.has(payload.s) ? payload.s : defaultSize;
        state.customWristMm = Math.round(Math.min(customWristMax, Math.max(customWristMin, number(payload.w) || state.customWristMm)));
        const cordButton = materialButtons.find((button) => button.dataset.kind === 'cord' && button.dataset.key === payload.c);
        if (cordButton) state.cord = { key: cordButton.dataset.key, name: cordButton.dataset.name, visual: cordButton.dataset.visual || '', price: number(cordButton.dataset.price) };
        state.designName = String(payload.n || state.designName).slice(0, 64);
        state.selectedId = null;
        state.requestId = '';
        sharedDesignToken = '';
        root.dataset.sharedDesignLoaded = 'true';
        if (history.replaceState) history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
        return true;
      } catch {
        return false;
      }
    };

    const remember = (savedSnapshot = snapshot()) => {
      state.history.push(savedSnapshot);
      if (state.history.length > 30) state.history.shift();
      state.requestId = '';
      if (commerceStatus) commerceStatus.hidden = true;
      if (cartLink) cartLink.hidden = true;
      if (wristHost) delete wristHost.dataset.ready;
    };

    const restoreSnapshot = (saved) => {
      if (!saved || !Array.isArray(saved.items)) return false;
      state.items = cloneItems(saved.items);
      const legacyWristMm = number(saved.wrist) * 10;
      const migratedSize = legacyWristMm > 0 ? (legacyWristMm <= 155 ? 'S' : legacyWristMm <= 175 ? 'M' : 'L') : defaultSize;
      state.size = saved.size === customSize || sizePresets.has(saved.size) ? saved.size : migratedSize;
      state.customWristMm = Math.round(Math.min(customWristMax, Math.max(
        customWristMin,
        number(saved.customWristMm) || number(saved.wristMm) || state.customWristMm
      )));
      state.customAutoFit = Boolean(saved.customAutoFit && state.size === customSize);
      state.cord = saved.cord || state.cord;
      state.selectedId = saved.selectedId || null;
      state.designName = String(saved.designName || state.designName).slice(0, 64);
      state.rotationRad = Number.isFinite(Number(saved.rotationRad)) ? Number(saved.rotationRad) : 0;
      state.zoom = saved.zoomMode === 'relative-fit' ? clampZoom(number(saved.zoom) || 1) : 1;
      state.requestId = /^[a-zA-Z0-9-]{16,80}$/.test(String(saved.requestId || '')) ? String(saved.requestId) : '';
      return true;
    };

    const currentPreset = () => {
      if (state.size === customSize) {
        return {
          size: customSize,
          wristMin: state.customWristMm,
          wristMax: state.customWristMm,
          targetMm: state.customWristMm + comfortAllowanceMm,
          isCustom: true
        };
      }
      return sizePresets.get(state.size) || sizePresets.get(defaultSize);
    };

    const measureItems = (items) => {
      const usedMm = items.reduce((sum, item) => roundedMillimeterSum(sum, physicalOccupation(item)), 0);
      const weightedThickness = items.reduce(
        (sum, item) => roundedMillimeterSum(sum, physicalOccupation(item) * materialHeight(item)),
        0
      );
      const averageThicknessMm = usedMm > 0 ? Number((weightedThickness / usedMm).toFixed(2)) : 0;
      const gapLossMm = Math.PI * averageThicknessMm * 1.15;
      // Keep the legacy field for saved-draft compatibility, but expose the
      // physical along-string length as the same value everywhere.  The
      // previous gap-loss estimate made a 49 mm design appear as 11 mm.
      const innerWristMm = usedMm;
      return { usedMm, averageThicknessMm, gapLossMm, innerWristMm };
    };

    const selectedWristMm = (preset) => (
      preset.isCustom ? preset.wristMin : Math.round((preset.wristMin + preset.wristMax) / 2)
    );

    const fitStatusFor = (designLengthMm, preset) => {
      if (designLengthMm < customWristMin) return 'addBeads';
      const differenceMm = designLengthMm - selectedWristMm(preset);
      if (differenceMm < 0) return 'tooTight';
      if (differenceMm < 4) return 'snug';
      if (differenceMm <= 12) return 'good';
      if (differenceMm <= 18) return 'loose';
      return 'tooLoose';
    };

    // One recommendation feeds the sizing card, review and purchase notice.
    const fitGuidance = (current) => {
      if (!state.items.length) return labels.addBeads;
      if (state.items.every(isPendant)) {
        return `Your design fits about ${current.usedMm} mm. Add beads to build your bracelet — about ${Math.ceil(selectedWristMm(current.preset) / 8)} × 8 mm beads for ${current.preset.isCustom ? 'your wrist' : current.preset.size}.`;
      }
      const preset = current.preset;
      const sizeLabel = preset.isCustom ? `${labels.custom} ${preset.wristMin} mm` : preset.size;
      const selectedMm = selectedWristMm(preset);
      const upperMm = selectedMm + 18;
      const occupiedByDiameter = new Map();
      state.items.filter((item) => item.kind === 'bead').forEach((item) => {
        const diameter = beadDiameter(item);
        if (diameter) occupiedByDiameter.set(diameter, (occupiedByDiameter.get(diameter) || 0) + physicalOccupation(item));
      });
      const diameter = [...occupiedByDiameter].sort((a, b) => b[1] - a[1])[0]?.[0]
        || 8;
      const tooShort = ['addBeads', 'tooTight'].includes(current.fitStatus);
      if (tooShort || current.fitStatus === 'tooLoose') {
        const changeMm = Math.max(1, tooShort ? selectedMm - current.usedMm : current.usedMm - upperMm);
        const count = Math.ceil(changeMm / diameter);
        let text = (tooShort ? labels.short : labels.long)
          .replace('__ESTIMATE__', String(current.usedMm))
          .replace('__SIZE__', sizeLabel)
          .replace('__NEED__', String(tooShort ? selectedMm : upperMm))
          .replace('__CHANGE__', String(changeMm))
          .replace('__COUNT__', String(count))
          .replace('__DIAMETER__', String(diameter))
          .replace('__BEADS__', count === 1 ? 'bead' : 'beads');
        if (!allowAnyFit()) {
          text += ' ' + (tooShort ? labels.fitOrderMinimum : labels.fitOrderMaximum)
            .replace('__NEED__', String(tooShort ? selectedMm : upperMm));
        }
        return text;
      }
      return `${labels[current.fitStatus]} · ${labels.designFit} about ${current.usedMm} mm`;
    };

    const fitAllowsPurchase = (current) => state.items.length > 0
      && (allowAnyFit() || ['snug', 'good', 'loose'].includes(current.fitStatus));

    const totals = () => {
      const preset = currentPreset();
      const itemPrice = state.items.reduce((sum, item) => sum + item.price, 0);
      const measurements = measureItems(state.items);
      const weight = state.items.reduce((sum, item) => sum + item.weight, 0);
      return {
        price: itemPrice + state.cord.price,
        ...measurements,
        weight,
        selectedTargetMm: preset.targetMm,
        capacityMm: Math.max(preset.targetMm, measurements.usedMm),
        fitStatus: fitStatusFor(measurements.usedMm, preset),
        preset
      };
    };

    const isPendant = isPendantItem;
    const beadVisual = (item, interactive) => {
      const bead = document.createElement(interactive ? 'button' : 'span');
      if (interactive) {
        bead.type = 'button';
        bead.dataset.beadId = item.id;
        bead.setAttribute('aria-label', item.name);
      }
      bead.className = `mel-designer__bead mel-designer__bead--${item.kind}`;
      if(isPendant(item))bead.classList.add('mel-designer__bead--charm');
      bead.dataset.shape = item.shape || 'sphere';
      bead.dataset.materialType = item.materialType || '';
      bead.dataset.transparentReady = item.transparentImage ? 'true' : 'false';
      bead.dataset.catalogState = item.catalogState || 'current';
      if (item.visual) bead.classList.add(`mel-designer__bead--${item.visual}`);
      if (item.catalogState === 'unavailable') bead.classList.add('is-unavailable');
      if (item.id === state.selectedId && interactive) bead.classList.add('is-selected');
      // Render a mineral-toned placeholder before the remote image is ready.
      // This keeps a saved design visually complete during a slow catalog/CDN
      // response and leaves the hit target in the same position.
      const placeholder = document.createElement('span');
      placeholder.className = 'mel-designer__bead-placeholder';
      placeholder.setAttribute('aria-hidden', 'true');
      bead.classList.add('is-image-loading');
      bead.append(placeholder);
      const legacyProxyImage = !item.transparentImage && String(item.image || '').includes('assets.myastris.com')
        ? catalogTransparentUrl(item.image, 336)
        : item.image;
      const renderImage = item.transparentImage || legacyProxyImage;
      if (renderImage) {
        const image = document.createElement('img');
        image.crossOrigin = 'anonymous';
        image.alt = '';
        image.width = 256;
        image.height = 256;
        image.loading = interactive ? 'eager' : 'lazy';
        image.fetchPriority = interactive ? 'high' : 'auto';
        image.decoding = 'async';
        image.draggable = false;
        const reveal = () => {
          bead.classList.remove('is-image-loading');
          if(isPendant(item) && !bead.querySelector('.mel-designer__pendant-body')) {
            const source=document.createElement('canvas');source.width=image.naturalWidth;source.height=image.naturalHeight;
            const context=source.getContext('2d');context.drawImage(image,0,0);
            try {
              const pixels=context.getImageData(0,0,source.width,source.height).data;
              const cut=Math.floor(source.height*(pendantProfiles[item.key]||[.2,.32])[1]);
              let left=source.width,right=0,top=source.height,bottom=0;
              for(let y=cut;y<source.height;y++)for(let x=0;x<source.width;x++)if(pixels[(y*source.width+x)*4+3]>36){left=Math.min(left,x);right=Math.max(right,x);top=Math.min(top,y);bottom=Math.max(bottom,y);}
              if(right>=left&&bottom>=top) {
                const body=document.createElement('canvas');body.className='mel-designer__pendant-body';body.width=right-left+1;body.height=bottom-top+1;
                body.getContext('2d').drawImage(source,left,top,body.width,body.height,0,0,body.width,body.height);
                bead.dataset.bodyHeightFraction=String(Math.min(item.height,(item.width||item.size)*body.height/body.width)/item.height);bead.dataset.alphaTrim=JSON.stringify([left,top,right,bottom]);
                bead.append(body);image.hidden=true;
                const ring=document.createElement('span');ring.className='mel-designer__pendant-ring';ring.setAttribute('aria-hidden','true');
                ring.dataset.metal=/silver|white|jt9pp|ef7TQ|pPIci|XhaLj|prFnD|nxM7X|YCZRH/i.test(`${item.name} ${item.key}`)?'silver':'gold';bead.append(ring);
                const placement=bead._melPlacement;
                if(placement)positionBead(bead,item,placement.index,placement.distance,placement.geometry,placement.middle);
              }
            } catch { bead.dataset.pendantTextureState='unavailable'; }
          }
        };
        const retainPlaceholder = () => {
          bead.classList.remove('is-image-loading');
          bead.classList.add('is-image-error');
        };
        image.addEventListener('load', reveal, { once: true });
        attachImageRetry(image, retainPlaceholder);
        // Keep the initial strand/review render on the bounded thumbnail route.
        // Full material images are reserved for an explicitly opened 3D/try-on
        // view so they cannot compete with the first material cards on mobile.
        const legacyProxyThumbnail = !item.transparentThumbnail && String(item.thumbnail || '').includes('assets.myastris.com')
          ? catalogThumbnailUrl(item.thumbnail, 112)
          : item.thumbnail;
        image.src = item.transparentThumbnail || (item.transparentImage
          ? catalogThumbnailUrl(item.transparentImage, 112)
          : (legacyProxyThumbnail || renderImage));
        image.dataset.catalogSrc = image.getAttribute('src') || '';
        bead.append(image);
      } else {
        bead.classList.remove('is-image-loading');
        bead.classList.add('is-image-error');
      }
      return bead;
    };

    const pendantProfiles = {
      GPK001:[.22,.29],SPK001:[.22,.29],XOe77:[.22,.25],jt9pp:[.22,.19],ef7TQ:[.20,.20],
      pPIci:[.22,.32],d9C1T:[.22,.36],XhaLj:[.22,.36],SDOSc:[.15,.15],prFnD:[.22,.38],
      '552Lb':[.22,.35],UVlvE:[.24,.36],nxM7X:[.22,.21],YCZRH:[.20,.22],GaaIp:[.17,.24]
    };
    const displayOccupation = physicalOccupation;
    const orbitLayout = (items = state.items) => {
      const usedMm=items.reduce((sum,item)=>roundedMillimeterSum(sum,displayOccupation(item)),0);
      const targetMm=currentPreset().targetMm;
      // Beads spread evenly round the ring, as ASTRIS does for an unmeasured bracelet. The ring is
      // never shorter than its beads, so beads do not overlap. An empty design keeps the minimum ring.
      const circumferenceMm=usedMm>0?Math.max(targetMm,usedMm):minimumOrbitMm;
      const gapMm=0;
      // Half the widest bead. A charm's loop sits on the bead rim and its body hangs outside it.
      const beadRimMm=items.reduce((largest,item)=>(isPendantItem(item)?largest:Math.max(largest,number(item.size)*.49)),0);
      return { circumferenceMm, beadRimMm, gapMm };
    };

    // Gap g sits just before bead g in ring order; gap 0 is the join between the last bead and the first.
    // A charm hangs in the gap after the beads before it in the list, so where it is dropped sets its gap.
    const charmGapAt = (items, index) => {
      const beadCount = items.filter((item) => !isPendantItem(item)).length;
      if (beadCount === 0) return 0;
      const before = items.slice(0, index).filter((item) => !isPendantItem(item)).length;
      return before % beadCount;
    };
    const fractionGap = (first, second) => {
      const delta = Math.abs(first - second) % 1;
      return Math.min(delta, 1 - delta);
    };
    // Charms may hang only in gaps between beads, one charm to a gap. A design with no beads is a charm ring.
    const charmsFit = (items) => {
      if (items.every(isPendantItem)) return true;
      const beadCount = items.filter((item) => !isPendantItem(item)).length;
      if (beadCount < 2) return false;
      const gaps = items.flatMap((item, index) => (isPendantItem(item) ? [charmGapAt(items, index)] : []));
      return gaps.length <= beadCount && new Set(gaps).size === gaps.length;
    };
    // Where an item can be put round the ring, as { index, fraction }: index is a list position with
    // movingId left out, and fraction is the position round the ring from 0 to 1. A bead goes before
    // any bead, or at the end. A charm goes into any gap that does not hold a charm yet.
    const dropCandidates = (items, charm, movingId = '') => {
      const rest = items.filter((item) => item.id !== movingId);
      const beadIndexes = rest.flatMap((item, index) => (isPendantItem(item) ? [] : [index]));
      const beadCount = beadIndexes.length;
      if (!charm) {
        if (beadCount === 0) return [{ index: rest.length, fraction: 0 }];
        return [...beadIndexes.map((index, bead) => ({ index, fraction: bead / beadCount })), { index: rest.length, fraction: 0 }];
      }
      if (beadCount === 0) {
        const charmIndexes = rest.flatMap((item, index) => (isPendantItem(item) ? [index] : []));
        const total = charmIndexes.length + 1;
        return [...charmIndexes.map((index, ordinal) => ({ index, fraction: (ordinal + 0.5) / total })), { index: rest.length, fraction: (charmIndexes.length + 0.5) / total }];
      }
      if (beadCount < 2) return [];
      const taken = new Set(rest.flatMap((item, index) => (isPendantItem(item) ? [charmGapAt(rest, index)] : [])));
      return beadIndexes.flatMap((index, gap) => (taken.has(gap) ? [] : [{ index, fraction: gap / beadCount }]));
    };
    // A charm added with a tap goes into the free gap farthest from the charms already there.
    const tapCharmCandidate = (items) => {
      const candidates = dropCandidates(items, true);
      if (candidates.length === 0) return null;
      const beadCount = items.filter((item) => !isPendantItem(item)).length;
      if (beadCount === 0) return candidates[candidates.length - 1];
      const taken = items.flatMap((item, index) => (isPendantItem(item) ? [charmGapAt(items, index) / beadCount] : []));
      if (taken.length === 0) return candidates[0];
      const spread = (candidate) => Math.min(...taken.map((fraction) => fractionGap(candidate.fraction, fraction)));
      return candidates.reduce((best, candidate) => (spread(candidate) > spread(best) ? candidate : best));
    };
    // Where each item sits round the ring, in mm, laid out the way ASTRIS lays out an unmeasured bracelet.
    // Beads spread evenly round the whole ring in list order. A charm that fits hangs in its gap; a design
    // with no beads, or with charms that do not fit, spreads its charms evenly round the whole ring.
    const ringSlots = (items, layout) => {
      const circumferenceMm = layout.circumferenceMm;
      const beadIndexes = [];
      items.forEach((item, index) => { if (!isPendantItem(item)) beadIndexes.push(index); });
      const beadCount = beadIndexes.length;
      const charmCount = items.length - beadCount;
      const inGaps = charmsFit(items);
      const slots = [];
      beadIndexes.forEach((index, bead) => { slots[index] = { mm: ((bead + 0.5) / beadCount) * circumferenceMm }; });
      let charmOrdinal = 0;
      items.forEach((item, index) => {
        if (!isPendantItem(item)) return;
        if (inGaps && beadCount > 0) slots[index] = { mm: (charmGapAt(items, index) / beadCount) * circumferenceMm };
        else slots[index] = { mm: ((charmOrdinal + 0.5) / charmCount) * circumferenceMm };
        charmOrdinal += 1;
      });
      return slots;
    };

    const bestFitZoom = () => {
      const { circumferenceMm } = orbitLayout();
      const largestItem = state.items.reduce((largest, item) => Math.max(largest, item.size * 1.85), 20);
      // Leave room around the ring for charms and the stage's side controls.
      const outerDiameterPx = Math.max(62, circumferenceMm / Math.PI + Math.max(largestItem, pendantOuterMm(state.items))) * pixelsPerMm * (state.items.some(isPendantItem) ? 1.14 : 1);
      return Math.max(0.05, Math.min(1.2, (Math.min(canvas.clientWidth, canvas.clientHeight) - 48) / outerDiameterPx));
    };

    const fitOrbitZoom = () => { canvas.dataset.fitScale = String(bestFitZoom()); };

    const orbitGeometry = (targetCanvas) => {
      const layoutTotals = totals();
      const layout = orbitLayout();
      const canvasWidth = targetCanvas.clientWidth || canvas.clientWidth || Math.min(window.innerWidth - 48, 360);
      const canvasHeight = targetCanvas.clientHeight || canvasWidth;
      const canvasSize = Math.min(canvasWidth, canvasHeight);
      const largestItem = state.items.reduce((largest, item) => Math.max(largest, item.size), 10);
      const physicalOuterDiameter = Math.max(62, (layout.circumferenceMm / Math.PI) + Math.max(12, largestItem * 1.18, pendantOuterMm(state.items)));
      const mmToPx = targetCanvas === canvas ? pixelsPerMm * bestFitZoom() * state.zoom : (canvasSize * 0.78) / physicalOuterDiameter;
      const radiusPx = (layout.circumferenceMm / (Math.PI * 2)) * mmToPx;
      return { layoutTotals, ...layout, mmToPx, radiusPx };
    };

    const orbitCenterOffsetY = () => number(getComputedStyle(canvas).getPropertyValue('--orbit-center-offset-y'));

    const materialInsertionPlacement = (clientX, clientY, button, rect, geometry) => {
      const centerOffsetY = orbitCenterOffsetY();
      const x = clientX - rect.left - rect.width / 2;
      const y = clientY - rect.top - rect.height / 2 - centerOffsetY;
      let pointerAngle = Math.atan2(y, x) + Math.PI / 2 - state.rotationRad;
      while (pointerAngle < 0) pointerAngle += Math.PI * 2;
      while (pointerAngle >= Math.PI * 2) pointerAngle -= Math.PI * 2;
      const pointerFraction = pointerAngle / (Math.PI * 2);
      const charm = isPendantItem(itemFromButton(button));
      const candidates = dropCandidates(state.items, charm);
      const boundary = candidates.reduce((nearest, candidate) => (
        fractionGap(candidate.fraction, pointerFraction) < fractionGap(nearest.fraction, pointerFraction) ? candidate : nearest
      ), candidates[0] || { index: state.items.length, fraction: pointerFraction });
      const angle = -Math.PI / 2 + state.rotationRad + boundary.fraction * Math.PI * 2;
      const materialSize = Math.max(1, number(button?.dataset.size) || 10);
      const visualScale = button?.dataset.kind === 'charm' ? 0.9 : button?.dataset.kind === 'spacer' ? 1 : 0.98;
      const displaySize = materialSize * geometry.mmToPx * visualScale;
      return {
        index: boundary.index,
        left: rect.width / 2 + Math.cos(angle) * geometry.radiusPx,
        top: rect.height / 2 + centerOffsetY + Math.sin(angle) * geometry.radiusPx,
        size: displaySize
      };
    };

    const positionBead = (bead, item, index, distance, geometry, middle) => {
      bead._melPlacement={index,distance,geometry,middle};
      const angle = -Math.PI / 2 + state.rotationRad + (middle / geometry.circumferenceMm) * Math.PI * 2;
      const visualScale = item.kind === 'bead' ? .98 : item.kind === 'charm' ? charmScale : 1;
      const displaySize = item.size * geometry.mmToPx * visualScale;
      const physicalWidth = Math.max(1, number(item.width) || item.size);
      const physicalHeight = Math.max(1, number(item.height) || physicalWidth);
      const bodyFraction=isPendant(item) ? number(bead.dataset.bodyHeightFraction)||(1-(pendantProfiles[item.key]||[.2,.32])[1]) : 1;
      const displayWidth = physicalWidth*geometry.mmToPx*visualScale;
      const displayHeight = physicalHeight*bodyFraction*geometry.mmToPx*visualScale;
      bead.style.setProperty('--bead-size', `${displaySize}px`);
      bead.style.setProperty('--bead-width', `${displayWidth}px`);
      bead.style.setProperty('--bead-height', `${displayHeight}px`);
      const hookY=(pendantProfiles[item.key]||[.2,.32])[0];
      const mountOverlap=Math.min(1.25,physicalHeight*bodyFraction*.1);
      // The loop sits on the bead rim and the body hangs outside it. The loop's offset is measured in the charm's own turned frame.
      const charmTurn = isPendant(item) ? charmSwing * Math.sin(angle) : 0;
      const loopDistance = displayHeight/2+1.2*geometry.mmToPx;
      const outward=isPendant(item) ? (geometry.beadRimMm||0)*geometry.mmToPx+loopDistance : 0;
      bead.style.setProperty('--ring-dx', isPendant(item) ? `${-loopDistance*Math.cos(angle-charmTurn)}px` : '0px');
      bead.style.setProperty('--ring-dy', isPendant(item) ? `${-loopDistance*Math.sin(angle-charmTurn)}px` : '0px');
      bead.style.setProperty('--pendant-ring-size', `${4*geometry.mmToPx}px`);
      bead.style.setProperty('--pendant-wire-size', `${.45*geometry.mmToPx}px`);
      bead.style.setProperty('--pendant-ring-top', `${(-4+mountOverlap)*geometry.mmToPx}px`);
      const orbitRadius=geometry.radiusPx+outward;
      const x=Math.cos(angle)*orbitRadius,y=Math.sin(angle)*orbitRadius;
      bead.style.translate=`${x}px ${y}px`;
      bead.style.setProperty('--bead-hang',`${outward}px`);
      if(isPendant(item)) {
        bead.style.rotate=`${charmTurn}rad`;
        bead.dataset.pendantGeometry=JSON.stringify({ringX:Math.cos(angle)*geometry.radiusPx,ringY:Math.sin(angle)*geometry.radiusPx,
          bodyX:x,bodyY:y,radialAngle:angle,bodyAngle:angle,hookY,ringLengthMm:positiveNumber(item.ringLengthMm)||5});
      } else bead.style.removeProperty('rotate');
      beadPositions.set(bead, { x, y });
      bead.style.setProperty('--bead-index', index);
    };

    const positionExistingOrbit = (orderedItems, draggedId = '') => {
      const geometry = orbitGeometry(canvas);
      canvas.style.setProperty('--orbit-diameter', `${geometry.radiusPx * 2}px`);
      const nodes = new Map([...layer.querySelectorAll('[data-bead-id]')].map((node) => [node.dataset.beadId, node]));
      const slots = ringSlots(orderedItems, geometry);
      let distance = 0;
      orderedItems.forEach((item, index) => {
        const node = nodes.get(item.id);
        if (node && item.id !== draggedId) positionBead(node, item, index, distance, geometry, slots[index].mm);
        distance += displayOccupation(item) + geometry.gapMm;
      });
    };

    const renderOrbit = (targetCanvas, targetLayer, interactive, geometry = orbitGeometry(targetCanvas)) => {
      const focusedBead = interactive && targetLayer.contains(document.activeElement) ? document.activeElement : null;
      targetCanvas.style.setProperty('--orbit-diameter', `${geometry.radiusPx * 2}px`);
      let distance = 0;
      const existingNodes = interactive
        ? new Map([...targetLayer.querySelectorAll('[data-bead-id]')].map((node) => [node.dataset.beadId, node]))
        : new Map();
      const activeIds = new Set(state.items.map((item) => item.id));

      if (!interactive) targetLayer.replaceChildren();
      if (interactive) {
        existingNodes.forEach((node, itemId) => {
          if (activeIds.has(itemId)) return;
          existingNodes.delete(itemId);
          pendingFlightIds.delete(itemId);
          pendingArrivalOrigins.delete(itemId);
          if (node.classList.contains('is-leaving')) return;
          node.classList.add('is-leaving');
          node.disabled = true;
          node.removeAttribute('data-bead-id');
          node.getAnimations().forEach((animation) => animation.cancel());
          node.animate(
            [{ opacity: 1, transform: 'translate(-50%, -50%) scale(1)' }, { opacity: 0, transform: 'translate(-50%, -50%) scale(0.35)' }],
            { duration: reducedMotion ? 1 : 180, easing: 'ease-in', fill: 'forwards' }
          ).finished.catch(() => null).then(() => node.remove());
        });
      }

      const slots = ringSlots(state.items, geometry);
      state.items.forEach((item, index) => {
        const bead = existingNodes.get(item.id) || beadVisual(item, interactive);
        if (interactive) {
          bead.classList.toggle('is-selected', item.id === state.selectedId);
          bead.classList.toggle('is-unavailable', item.catalogState === 'unavailable');
          bead.dataset.catalogState = item.catalogState || 'current';
          bead.setAttribute('aria-label', item.name);
        }
        positionBead(bead, item, index, distance, geometry, slots[index].mm);
        const nextNode = targetLayer.children[index];
        if (nextNode !== bead) targetLayer.insertBefore(bead, nextNode || null);
        if (interactive && !existingNodes.has(item.id)) {
          if (pendingFlightIds.has(item.id) && !reducedMotion) {
            bead.classList.add('is-flight-pending');
          } else {
            bead.classList.add('is-entering');
            window.setTimeout(() => bead.classList.remove('is-entering'), reducedMotion ? 1 : 480);
          }
        }
        distance += displayOccupation(item) + geometry.gapMm;
      });

      if (interactive) bindBeads();
      if (focusedBead?.isConnected && document.activeElement !== focusedBead) focusedBead.focus({ preventScroll: true });
    };

    const threeFallbackMessage = '3D is unavailable on this device. Your 2D design is shown.';
    const wristFallbackMessage = 'Wrist try-on is unavailable. Your 2D design preview is shown.';
    const renderWristFallback = (error) => {
      if (!wristFallback) return;
      previewCanvas && (previewCanvas.hidden = true);
      wristHost && (wristHost.hidden = true);
      if (wristHost) {
        wristHost.dataset.state = 'fallback';
        delete wristHost.dataset.ready;
      }
      wristFallback.hidden = false;
      wristFallbackNote && (wristFallbackNote.textContent = wristFallbackMessage);
      if (wristFallbackCanvas && wristFallbackLayer) {
        renderOrbit(wristFallbackCanvas, wristFallbackLayer, false);
        if (wristFallbackCord) wristFallbackCord.dataset.cord = state.cord.visual;
      }
      if (error instanceof Error) root.dataset.wristError = error.message.slice(0, 180);
      root.dataset.wristState = 'fallback';
      wristComingNote && (wristComingNote.hidden = true);
      wristTab && (wristTab.disabled = false);
      wristTab?.removeAttribute('aria-disabled');
      // The fallback panel already carries this message. Writing it into the
      // stage's three-note too stacked two copies on top of each other.
      if (typeof threeNote !== 'undefined' && threeNote) threeNote.textContent = '';
    };

    const updateActionState = () => {
      const hasItems = state.items.length > 0;
      const current = totals();
      const stageMetric = (name, text) => root.querySelectorAll(`[data-stage-${name}]`).forEach((node) => { node.textContent = text; });
      stageMetric('length', current.usedMm > 0 ? `${(current.usedMm / 10).toFixed(1)} cm` : '—');
      stageMetric('beads', String(state.items.length));
      stageMetric('total', currency.format(current.price));
      const fitAllowsCommerce = fitAllowsPurchase(current);
      const outsideRecommendedFit = !['snug', 'good', 'loose'].includes(current.fitStatus);
      const catalogIssues = catalogReady ? getCatalogIssues() : [];
      const canTransact = catalogReady && catalogIssues.length === 0;
      actionButtons.forEach((button) => {
        const action = button.dataset.designerAction;
        if (action === 'undo') button.disabled = state.history.length === 0;
        if (['clear', 'review', 'save', 'save-dialog'].includes(action)) button.disabled = !hasItems;
        if (['confirm', 'add-to-cart'].includes(action)) button.disabled = !fitAllowsCommerce || state.commerceBusy || !canTransact;
        if (action === 'zoom-out') button.disabled = state.zoom <= minimumZoom;
        if (action === 'zoom-in') button.disabled = state.zoom >= maximumZoom;
      });
      if (fitCheckoutNote) {
        // The review panel owns the single fit suggestion above its material list.
        // Keep the editor warning available, but never duplicate it inside review.
        fitCheckoutNote.hidden = Boolean(dialog?.open) || !hasItems || !outsideRecommendedFit;
        fitCheckoutNote.classList.toggle('is-fit-warning', hasItems && outsideRecommendedFit);
        fitCheckoutNote.textContent = fitGuidance(current);
      }
      updateCatalogNotice(catalogIssues);
      const selectedIndex = state.items.findIndex((item) => item.id === state.selectedId);
      selection.hidden = selectedIndex < 0;
      if (selectedIndex >= 0) {
        selectedName.textContent = state.items[selectedIndex].name;
        const previous = selection.querySelector('[data-designer-action="move-previous"]');
        const next = selection.querySelector('[data-designer-action="move-next"]');
        previous.disabled = selectedIndex === 0;
        next.disabled = selectedIndex === state.items.length - 1;
      }
    };

    const updateCord = () => {
      cord.dataset.cord = state.cord.visual;
      previewCord.dataset.cord = state.cord.visual;
      materialButtons.filter((button) => button.dataset.kind === 'cord').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.key === state.cord.key);
      });
    };

    let userAdjustedZoom = false;
    const setZoom = (nextZoom) => {
      if (!Number.isFinite(nextZoom)) return;
      const zoom = clampZoom(nextZoom);
      if (zoom === state.zoom) return;
      userAdjustedZoom = true;
      state.zoom = zoom;
      canvas.classList.add('is-zooming');
      window.clearTimeout(zoomEndTimer);
      zoomEndTimer = window.setTimeout(() => canvas.classList.remove('is-zooming'), 150);
      if (zoomFrame) return;
      zoomFrame = window.requestAnimationFrame(() => {
        zoomFrame = 0;
        positionExistingOrbit(state.items);
        scheduleDraft();
        if (zoomValue) zoomValue.textContent = `${Math.round(state.zoom * 100)}%`;
        actionButtons.forEach((button) => {
          if (button.dataset.designerAction === 'zoom-out') button.disabled = state.zoom <= minimumZoom;
          if (button.dataset.designerAction === 'zoom-in') button.disabled = state.zoom >= maximumZoom;
        });
      });
    };

    canvas.addEventListener('wheel', (event) => {
      if (!(event.ctrlKey || event.metaKey) || !Number.isFinite(event.deltaY) || !event.deltaY) return;
      if (canvas.classList.contains('is-dragging') || canvas.classList.contains('is-rotating') || activeMaterialDragButton) return;
      const units = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? canvas.clientHeight : 1;
      event.preventDefault();
      event.stopPropagation();
      setZoom(state.zoom * Math.pow(0.999, event.deltaY * units));
    }, { passive: false });

    let pinchStart = null;
    const touchDistance = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
    canvas.addEventListener('touchstart', (event) => {
      if (event.touches.length === 2) pinchStart = { distance: touchDistance(event.touches), zoom: state.zoom };
    }, { passive: true });
    canvas.addEventListener('touchmove', (event) => {
      if (!pinchStart || event.touches.length !== 2) return;
      event.preventDefault();
      setZoom(pinchStart.zoom * touchDistance(event.touches) / Math.max(1, pinchStart.distance));
    }, { passive: false });
    canvas.addEventListener('touchend', () => { pinchStart = null; }, { passive: true });

    const render = () => {
      if (renderFrame) window.cancelAnimationFrame(renderFrame);
      renderFrame = 0;
      const geometry = orbitGeometry(canvas);
      const current = totals();
      priceOutput.textContent = currency.format(current.price);
      if (mobileReviewPrice) mobileReviewPrice.textContent = currency.format(current.price);
      lengthOutput.textContent = `${current.usedMm} mm`;
      lengthOutput.dataset.designLengthMm = String(current.usedMm);
      countOutput.textContent = String(state.items.length);
      countOutput.dataset.designBeadCount = String(state.items.length);
      if (zoomValue) zoomValue.textContent = `${Math.round(state.zoom * 100)}%`;
      if (designNameInput && document.activeElement !== designNameInput) designNameInput.value = state.designName;
      const idealFitMm = selectedWristMm(current.preset) + preferredEaseMm;
      progress.style.width = `${Math.min(100, (current.usedMm / idealFitMm) * 100)}%`;
      progress.parentElement.classList.toggle(
        'is-over',
        ['tooTight', 'tooLoose'].includes(current.fitStatus)
      );
      sizeButtons.forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.size === state.size)));
      customSizeControl?.classList.toggle('is-active', state.size === customSize);
      if (customSizeControl) customSizeControl.dataset.active = String(state.size === customSize);
      syncWristUnit();
      fitSize.textContent = current.preset.isCustom ? 'C' : current.preset.size;
      fitRange.textContent = current.preset.isCustom
        ? `${current.preset.wristMin} mm`
        : `${current.preset.wristMin}–${current.preset.wristMax} mm`;
      if (fitRecommended) fitRecommended.textContent = `Recommended fit ${selectedWristMm(current.preset)} mm`;
      fitTarget.textContent = fitGuidance(current);
      fitTarget.dataset.designLengthMm = String(current.usedMm);
      const dominant = [...new Set(state.items.filter((item) => item.kind === 'bead').map((item) => item.key))]
        .map((key) => ({ key, count: state.items.filter((item) => item.key === key).length }))
        .sort((a, b) => b.count - a.count)[0];
      const mainBead = state.items.find((item) => item.key === dominant?.key);
      const beadMm = beadDiameter(mainBead || { size: 8 });
      const difference = selectedWristMm(current.preset) + preferredEaseMm - current.usedMm;
      const advice = !state.items.length ? 'Choose beads to start, or try a template.'
        : current.fitStatus === 'tooLoose' ? `Remove about ${Math.ceil((current.usedMm - selectedWristMm(current.preset) - 12) / beadMm)} beads for a comfortable fit.`
          : ['snug', 'good', 'loose'].includes(current.fitStatus) ? labels[current.fitStatus]
            : `Add about ${Math.max(1, Math.ceil(difference / beadMm))} beads for a comfortable fit.`;
      const fullAdvice = fitGuidance(current);
      const compactAdvice = !state.items.length ? 'Add beads to fit'
        : ['snug', 'good', 'loose'].includes(current.fitStatus) ? labels[current.fitStatus]
          : current.fitStatus === 'tooLoose' ? `Remove ${Math.max(1, Math.ceil((current.usedMm - selectedWristMm(current.preset) - 12) / beadMm))} beads`
            : `Add ${Math.max(1, Math.ceil(difference / beadMm))} more beads`;
      const beadCountAdvice = current.fitStatus === 'tooLoose'
        ? Math.max(1, Math.ceil((current.usedMm - selectedWristMm(current.preset) - 12) / beadMm))
        : Math.max(1, Math.ceil(difference / beadMm));
      root.dataset.designLengthMm = String(current.usedMm);
      root.dataset.fitBeadDiameterMm = String(beadMm);
      root.dataset.fitBeadCount = String(beadCountAdvice);
      if (fitAdviceButton) {
        fitAdviceButton.dataset.fullAdvice = fullAdvice;
        fitAdviceButton.dataset.designLengthMm = String(current.usedMm);
        fitAdviceButton.dataset.beadDiameterMm = String(beadMm);
        fitAdviceButton.dataset.beadCount = String(beadCountAdvice);
        fitAdviceButton.textContent = fitAdviceButton.getAttribute('aria-expanded') === 'true' ? fullAdvice : compactAdvice;
        fitAdviceButton.title = fullAdvice;
        fitAdviceButton.setAttribute('aria-label', `Fit advice: ${fullAdvice}`);
      }
      const meter = root.querySelector('[data-fit-meter]');
      const idealUsed = selectedWristMm(current.preset) + preferredEaseMm;
      const fitPosition = Math.min(100, Math.max(0, current.usedMm / Math.max(1, idealUsed * 1.6) * 100));
      if (meter) {
        meter.setAttribute('aria-valuenow', String(Math.round(fitPosition)));
        meter.setAttribute('aria-valuetext', advice);
        meter.dataset.fitStatus = current.fitStatus;
        meter.style.setProperty('--fit-position', `${fitPosition}%`);
      }
      root.querySelector('[data-designer-action="custom-size"]')?.setAttribute('aria-pressed', String(state.size === customSize));
      const fillButton = root.querySelector('[data-designer-action="fill-to-fit"]');
      if (fillButton) fillButton.disabled = !catalogReady || ['snug', 'good', 'loose', 'tooLoose'].includes(current.fitStatus);
      fitRemaining.textContent = '';
      fitRemaining.hidden = true;
      root.querySelector('[data-fit-status]')?.classList.toggle('is-fit-warning', state.items.length > 0 && !['snug', 'good', 'loose'].includes(current.fitStatus));
      fitRemaining.classList.toggle(
        'is-over',
        ['tooTight', 'tooLoose'].includes(current.fitStatus)
      );
      empty.hidden = state.items.length > 0;
      renderOrbit(canvas, layer, true, geometry);
      canvas.dataset.layoutReady = 'true';
      canvas.dataset.renderSize = `${canvas.clientWidth}x${canvas.clientHeight}`;
      updateCord();
      updateMaterialUsage();
      updateActionState();
      updatePreviewThumbnail();
      if (draftInitialized) scheduleDraft();
    };

    const requestRender = () => {
      if (!renderFrame) renderFrame = window.requestAnimationFrame(render);
    };

    const maximumDesignMm = customWristMax + 50;

    const showCapacityLimit = () => {
      announce(messages.maxSize || messages.full);
      canvas.animate([
        { transform: 'translateX(0)' },
        { transform: 'translateX(-5px)' },
        { transform: 'translateX(5px)' },
        { transform: 'translateX(0)' }
      ], { duration: 260 });
    };

    const itemFromMaterial = (material) => ({
      id: `${material.key}-${Date.now()}-${itemSequence += 1}`,
      key: material.key,
      name: material.name,
      kind: material.kind,
      visual: material.visual || '',
      image: material.image || '',
      thumbnail: material.thumbnail || material.image || '',
      price: number(material.price),
      size: number(material.size),
      fitWidth: positiveNumber(material.fitWidth, material.calculatedWidth, material.width, material.size) || number(material.size),
      width: number(material.width) || number(material.size),
      height: number(material.height) || number(material.width) || number(material.size),
      weight: number(material.weight),
      stock: number(material.stock),
      shape: material.shape || 'sphere',
      materialType: material.materialType || ''
      ,transparentImage: material.transparentImage || ''
      ,transparentThumbnail: material.transparentThumbnail || material.transparentImage || ''
      ,hangOffsetMm: positiveNumber(material.hangOffsetMm)
    });

    const itemFromButton = (button) => itemFromMaterial({
      key: button.dataset.key,
      name: button.dataset.name,
      kind: button.dataset.kind,
      visual: button.dataset.visual,
      image: button.dataset.image,
      thumbnail: button.dataset.thumbnail,
      price: button.dataset.price,
      size: button.dataset.size,
      fitWidth: button.dataset.fitWidth,
      width: button.dataset.width,
      height: button.dataset.height,
      weight: button.dataset.weight,
      stock: button.dataset.stock,
      shape: button.dataset.shape,
      materialType: button.dataset.materialType
      ,transparentImage: button.dataset.transparentImage
      ,transparentThumbnail: button.dataset.transparentThumbnail
    });

    const safeCatalogUrl = (value) => {
      if (!value) return '';
      try {
        const url = new URL(String(value), window.location.origin);
        return url.protocol === 'https:' ? url.href : '';
      } catch {
        return '';
      }
    };

    // Keep source URLs in the catalog record for reconciliation, but route
    // every browser image request through the signed Shopify app proxy. The
    // proxy serves offline lossless WebP variants with source alpha preserved
    // for cards, strands, and 3D.
    const catalogAssetUrl = (source, width = 112) => {
      const safeSource = safeCatalogUrl(source);
      if (!safeSource || !commerceAssetEndpoint) return safeSource;
      try {
        const proxy = new URL(commerceAssetEndpoint, window.location.origin);
        if (safeSource.startsWith(`${proxy.origin}${proxy.pathname}`)) return safeSource;
        proxy.searchParams.set('source', safeSource);
        proxy.searchParams.set('width', String(width));
        proxy.searchParams.set('format', 'webp');
        return proxy.href;
      } catch {
        return safeSource;
      }
    };
    const catalogThumbnailUrl = (source, width = 112) => catalogAssetUrl(source, width);
    const catalogTransparentUrl = (source, width = 336) => catalogAssetUrl(source, width);

    const catalogKind = (materialType) => {
      if (materialType === 'Spacer') return 'spacer';
      if (materialType === 'Pendant') return 'charm';
      return 'bead';
    };

    const CJK_RE = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/u;
    const catalogNameOverrides = Object.freeze({
      'Halloween1003-25': 'Soul Keeper',
      'Halloween1003-7': 'DJ Skull'
    });
    const elementLabels = Object.freeze({ 水: 'Water', 火: 'Fire', 金: 'Metal', 土: 'Earth', 木: 'Wood' });
    const containsCjk = (value) => CJK_RE.test(String(value || ''));
    const englishCatalogName = (material) => {
      const code = String(material?.code || '').trim();
      if (catalogNameOverrides[code]) return catalogNameOverrides[code];
      for (const candidate of [material?.nameEn, material?.name]) {
        const value = String(candidate || '').replace(/\s+/g, ' ').trim();
        if (value && !containsCjk(value)) return value;
      }
      return '';
    };
    const englishElement = (value) => {
      const raw = String(value || '').trim();
      if (!raw || raw === '无') return '';
      if (elementLabels[raw]) return elementLabels[raw];
      return containsCjk(raw) ? '' : raw;
    };

    const catalogPricesInStoreCurrency = (pricing) => {
      if (!pricing || typeof pricing !== 'object') return null;
      const storeCurrency = String(root.dataset.currencyCode || '').toUpperCase();
      const baseCurrency = String(pricing.baseCurrency || '').toUpperCase();
      const sourceCurrency = String(pricing.currency || '').toUpperCase();
      if (baseCurrency === storeCurrency) {
        const finalPrice = parsedNumber(pricing.baseFinalPrice);
        if (finalPrice !== null && finalPrice >= 0) {
          return { finalPrice, originalPrice: parsedNumber(pricing.baseOriginalPrice) };
        }
      }
      if (sourceCurrency === storeCurrency) {
        const finalPrice = parsedNumber(pricing.finalPrice);
        if (finalPrice !== null && finalPrice >= 0) {
          return { finalPrice, originalPrice: parsedNumber(pricing.originalPrice) };
        }
      }
      if (sourceCurrency || baseCurrency) {
        throw new Error('Material catalog has no verified store-currency price');
      }
      return null;
    };

    const normalizeCatalogPayload = (payload) => {
      const data = payload?.data || payload;
      const materialsByType = data?.materialsByType;
      if (!materialsByType || typeof materialsByType !== 'object') return { types: [], items: [], hiddenCount: 0 };
      const rawTypes = Array.isArray(data.allTypes) && data.allTypes.length
        ? data.allTypes.map((type) => String(type))
        : Object.keys(materialsByType);
      const types = rawTypes.filter((type) => !containsCjk(type));
      const seen = new Set();
      const items = [];
      let hiddenCount = Number(data?.meta?.hiddenCount) || 0;

      types.forEach((materialType) => {
        const materials = Array.isArray(materialsByType[materialType]) ? materialsByType[materialType] : [];
        materials.forEach((material) => {
          const key = String(material?.code || '').trim();
          if (!key || seen.has(key) || number(material?.status) !== 1 || number(material?.stock) < 1) return;
          const name = englishCatalogName(material);
          if (!name) { hiddenCount += 1; return; }
          const imageUrls = Array.isArray(material?.imageUrls) ? material.imageUrls : [];
          const realImageUrls = Array.isArray(material?.realImageUrls) ? material.realImageUrls : [];
          const image = safeCatalogUrl(imageUrls.find(Boolean) || material?.thumbnailUrl);
          const thumbnail = safeCatalogUrl(material?.thumbnailUrl || image);
          const transparentImage = catalogTransparentUrl(image, 336);
          const transparentThumbnail = catalogTransparentUrl(thumbnail, 112);
          const quotedPrices = catalogPricesInStoreCurrency(material?.pricing);
          const finalPrice = quotedPrices?.finalPrice ?? null;
          const width = positiveNumber(material?.width, material?.diameter, material?.size, material?.calculatedWidth);
          const fitWidth = positiveNumber(material?.calculatedWidth, material?.width, material?.diameter, material?.size);
          const height = positiveNumber(material?.height, material?.calculatedHeight, material?.width, material?.diameter, material?.size, material?.calculatedWidth);
          const size = positiveNumber(material?.width, material?.diameter, material?.size, material?.calculatedWidth);
          if (finalPrice === null || finalPrice < 0 || width === null || fitWidth === null || height === null || size === null) { hiddenCount += 1; return; }
          const originalPriceCandidate = quotedPrices?.originalPrice ?? null;
          const originalPrice = originalPriceCandidate !== null && originalPriceCandidate >= finalPrice ? originalPriceCandidate : finalPrice;
          const kind = catalogKind(materialType);
          seen.add(key);
          const element = englishElement(material?.element);
          const subElement = englishElement(material?.subElement);
          items.push(Object.freeze({
            key, name, localName: name, materialType, kind,
            category: containsCjk(material?.category) ? '' : String(material?.category || ''),
            shape: containsCjk(material?.shape) ? 'sphere' : String(material?.shape || 'sphere'),
            element, subElement, price: finalPrice, originalPrice, size, fitWidth, width, height,
            weight: number(material?.weight), stock: number(material?.stock), image, thumbnail,
            transparentImage, transparentThumbnail,
            hangOffsetMm: kind === 'charm' ? Math.max(1, height * .3) : 0,
            realPhotoCount: realImageUrls.filter(Boolean).length,
            searchText: `${name} ${key} ${materialType} ${element} ${subElement}`.toLocaleLowerCase()
          }));
        });
      });
      return { types, items, hiddenCount };
    };

    const materialPlaceholderColor = (material) => {
      const text = `${material?.key || ''} ${material?.name || ''} ${material?.materialType || ''}`.toLocaleLowerCase();
      if (/amethyst|purple|紫/.test(text)) return '#876996';
      if (/rose|pink|rhodonite|粉|玫瑰/.test(text)) return '#d98a9d';
      if (/citrine|lemon|yellow|黄|柠檬/.test(text)) return '#d5bc72';
      if (/tiger|brown|bronze|虎眼|棕/.test(text)) return '#a8753e';
      if (/black|obsidian|黑/.test(text)) return '#252b33';
      if (/blue|lapis|topaz|蓝/.test(text)) return '#6689ae';
      if (/green|aventurine|jade|绿/.test(text)) return '#719b82';
      if (/clear|white|crystal|quartz|白|透明/.test(text)) return '#d8e3e4';
      if (/pearl|珍珠/.test(text)) return '#ece4dc';
      return '#c6a36a';
    };

    const prepareCatalogImage = (button, material, priority = false) => {
      const image = button?.querySelector('img[data-catalog-src]');
      if (!image) return;
      image.loading = priority ? 'eager' : 'lazy';
      image.fetchPriority = priority ? 'high' : 'auto';
      if (priority && image.dataset.catalogSrc && image.src !== image.dataset.catalogSrc) {
        image.src = image.dataset.catalogSrc;
        catalogImageObserver?.unobserve(image);
      }
      button.style.setProperty('--mel-material-color', materialPlaceholderColor(material));
    };

    // The material grid is its own scroll container.  A viewport-rooted
    // IntersectionObserver can miss a row revealed by an inner scroll on
    // WebKit and during a fast desktop scroll, leaving a visible placeholder
    // in the evidence frame.  Promote every card that is actually visible in
    // the panel as soon as its geometry is available.
    const ensureVisibleCatalogImages = (panel = catalogPanels.get(activeCategory())) => {
      if (!panel) return;
      [...panel.querySelectorAll('[data-material]')].forEach((button) => {
        const rect = button.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight || rect.right <= 0 || rect.left >= window.innerWidth) return;
        const image = button.querySelector('img[data-catalog-src]');
        if (!image || image.getAttribute('src') || !image.dataset.catalogSrc) return;
        image.loading = 'eager';
        image.fetchPriority = 'high';
        image.src = image.dataset.catalogSrc;
        catalogImageObserver?.unobserve(image);
      });
    };

    const createCatalogCard = (material, priority = false) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mel-designer__material';
      button.dataset.material = '';
      button.dataset.key = material.key;
      button.dataset.name = material.name;
      button.dataset.kind = material.kind;
      button.dataset.price = String(material.price);
      button.dataset.originalPrice = String(material.originalPrice);
      button.dataset.size = String(material.size);
      button.dataset.fitWidth = String(material.fitWidth);
      button.dataset.width = String(material.width);
      button.dataset.height = String(material.height);
      button.dataset.weight = String(material.weight);
      button.dataset.image = material.image;
      button.dataset.thumbnail = material.thumbnail;
      // Keep the rendered, alpha-preserving proxy variants on the card so
      // items added from the live catalogue carry the same URLs into the
      // strand renderer, 3D view, saved draft and review summary.
      button.dataset.transparentImage = material.transparentImage || '';
      button.dataset.transparentThumbnail = material.transparentThumbnail || '';
      button.dataset.shape = material.shape;
      button.dataset.materialType = material.materialType;
      button.dataset.stock = String(material.stock);
      button.style.setProperty('--mel-material-color', materialPlaceholderColor(material));
      button.title = `${material.name} · ${material.size} mm · ${currency.format(material.price)}`;
      button.setAttribute(
        'aria-label',
        `${material.name}, ${material.size} mm, ${currency.format(material.price)}. ${labels.dragMaterial}`
      );

      const swatch = document.createElement('span');
      swatch.className = 'mel-designer__swatch';
      swatch.dataset.shape = material.shape;
      if (material.thumbnail) {
        const image = document.createElement('img');
        image.crossOrigin = 'anonymous';
        image.alt = '';
        image.width = 256;
        image.height = 256;
        image.loading = priority ? 'eager' : 'lazy';
        image.fetchPriority = priority ? 'high' : 'auto';
        image.decoding = 'async';
        image.draggable = false;
        image.dataset.catalogSrc = catalogThumbnailUrl(material.thumbnail, 112);
        swatch.classList.add('is-loading');
        const reveal = () => swatch.classList.remove('is-loading');
        image.addEventListener('load', reveal, { once: true });
        attachImageRetry(image, reveal);
        if (priority) image.src = image.dataset.catalogSrc;
        else if (catalogImageObserver) catalogImageObserver.observe(image);
        else image.src = image.dataset.catalogSrc;
        swatch.append(image);
      }

      const copy = document.createElement('span');
      copy.className = 'mel-designer__material-copy';
      const name = document.createElement('strong');
      name.textContent = material.name;
      const details = document.createElement('small');
      details.className = 'mel-designer__material-price';
      const price = document.createElement('span');
      price.textContent = currency.format(material.price);
      details.append(price);
      if (material.originalPrice > material.price) {
        const originalPrice = document.createElement('del');
        originalPrice.textContent = currency.format(material.originalPrice);
        details.append(originalPrice);
      }
      const dimensions = document.createElement('small');
      dimensions.className = 'mel-designer__material-dimensions';
      dimensions.textContent = `${material.size} mm`;
      copy.append(name, dimensions, details);

      const add = document.createElement('span');
      add.className = 'mel-designer__material-add';
      add.setAttribute('aria-hidden', 'true');
      add.textContent = '+';

      const used = document.createElement('span');
      used.className = 'mel-designer__material-used';
      used.dataset.materialUse = '';
      used.hidden = true;

      button.append(swatch, copy, add, used);
      if (material.image) {
        const photo = document.createElement('span');
        photo.className = 'mel-designer__material-photo';
        photo.dataset.materialPhoto = '';
        photo.setAttribute('role', 'button');
        photo.tabIndex = 0;
        photo.setAttribute('aria-label', `View ${material.name}`);
        photo.title = `View ${material.name}`;
        photo.textContent = '';
        let photoPointerInside = true;
        const showPhoto = (event) => {
          event.preventDefault(); event.stopPropagation();
          // Chrome can retarget a nearby touch to this smaller control. Keep a
          // tap outside its actual box on the card's primary add action.
          if (event.type === 'click' && !photoPointerInside) {
            photoPointerInside = true;
            addMaterial(button);
            return;
          }
          const details = root.querySelector('[data-material-detail]');
          if (!details) return;
          details.querySelector('img').src = material.transparentImage || material.image;
          details.querySelector('[data-material-detail-name]').textContent = material.name;
          details.querySelector('[data-material-detail-spec]').textContent = `${material.size} mm · ${currency.format(material.price)}`;
          if (materialDetailCopy) materialDetailCopy.textContent = 'Traditionally associated with personal reflection and intention. This is symbolic context, not a promised effect.';
          details.showModal();
        };
        photo.addEventListener('click', showPhoto);
        photo.addEventListener('pointerdown', (event) => {
          const rect = photo.getBoundingClientRect();
          photoPointerInside = event.clientX >= rect.left && event.clientX <= rect.right
            && event.clientY >= rect.top && event.clientY <= rect.bottom;
          if (photoPointerInside) event.stopPropagation();
        });
        photo.addEventListener('keydown', (event) => { if (['Enter', ' '].includes(event.key)) showPhoto(event); });
        // Keep the detail hit target as a direct card child.  Nesting it in
        // the metadata grid let mobile row sizing push the icon below the
        // card and underneath the sticky Review & buy bar.
        button.append(photo);
      }
      return button;
    };

    const syncMaterialDraggable = (button) => {
      const enabled = Boolean(
        materialDropSlot &&
        !isCompactLayout() &&
        button.dataset.kind !== 'cord' &&
        !button.disabled
      );
      button.draggable = false;
      button.dataset.dragEnabled = String(enabled);
      if (button.dataset.kind !== 'cord') button.title = `${button.dataset.name} · ${button.dataset.size} mm · ${currency.format(number(button.dataset.price))}`;
    };

    const clearMaterialDrag = () => {
      activeMaterialDragButton?.classList.remove('is-drag-source');
      materialDragGhost?.remove();
      activeMaterialDragButton = null;
      materialDragGhost = null;
      materialDropIndex = null;
      if (materialDropSlot) {
        materialDropSlot.hidden = true;
        materialDropSlot.style.removeProperty('left');
        materialDropSlot.style.removeProperty('top');
        materialDropSlot.style.removeProperty('--drop-slot-size');
      }
      root.classList.remove('is-material-dragging');
      canvas.classList.remove('is-material-drop-target');
    };

    const bindMaterialButton = (button) => {
      if (boundMaterialButtons.has(button)) return;
      boundMaterialButtons.add(button);
      button.querySelectorAll('img').forEach((image) => { image.draggable = false; });
      syncMaterialDraggable(button);
      button.addEventListener('click', (event) => {
        if (performance.now() < suppressMaterialClickUntil) {
          event.preventDefault();
          return;
        }
        addMaterial(button);
      });
      button.addEventListener('pointerdown', (event) => {
        if (
          button.dataset.dragEnabled !== 'true' ||
          button.disabled ||
          event.pointerType === 'touch' ||
          (event.pointerType === 'mouse' && event.button !== 0)
        ) return;

        const pointerId = event.pointerId;
        const startX = event.clientX;
        const startY = event.clientY;
        let dragging = false;
        let overCanvas = false;
        let dragRect = null;
        let dragGeometry = null;
        let dragFrame = 0;
        let latestDragPointer = event;

        const updateDrag = (pointerEvent) => {
          if (materialDragGhost) {
            materialDragGhost.style.left = `${pointerEvent.clientX}px`;
            materialDragGhost.style.top = `${pointerEvent.clientY}px`;
          }
          const rect = dragRect;
          overCanvas = (
            pointerEvent.clientX >= rect.left && pointerEvent.clientX <= rect.right &&
            pointerEvent.clientY >= rect.top && pointerEvent.clientY <= rect.bottom
          );
          canvas.classList.toggle('is-material-drop-target', overCanvas);
          if (overCanvas) {
            const placement = materialInsertionPlacement(pointerEvent.clientX, pointerEvent.clientY, button, rect, dragGeometry);
            materialDropIndex = placement.index;
            if (materialDropSlot) {
              materialDropSlot.hidden = false;
              materialDropSlot.style.left = `${placement.left}px`;
              materialDropSlot.style.top = `${placement.top}px`;
              materialDropSlot.style.setProperty('--drop-slot-size', `${placement.size}px`);
            }
          } else {
            materialDropIndex = null;
            if (materialDropSlot) materialDropSlot.hidden = true;
          }
        };

        const beginDrag = (pointerEvent) => {
          dragRect = canvas.getBoundingClientRect();
          dragGeometry = orbitGeometry(canvas);
          dragging = true;
          activeMaterialDragButton = button;
          button.classList.add('is-drag-source');
          root.classList.add('is-material-dragging');
          const swatch = button.querySelector('.mel-designer__swatch, .mel-designer__cord-swatch');
          materialDragGhost = (swatch || button).cloneNode(true);
          materialDragGhost.classList.remove('is-loading');
          materialDragGhost.classList.add('mel-designer__material-ghost');
          materialDragGhost.setAttribute('aria-hidden', 'true');
          materialDragGhost.querySelectorAll('img').forEach((image) => { image.draggable = false; });
          document.body.append(materialDragGhost);
          updateDrag(pointerEvent);
        };

        const onMove = (moveEvent) => {
          if (moveEvent.pointerId !== pointerId) return;
          if (!dragging && Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) < 6) return;
          if (!dragging) beginDrag(moveEvent);
          moveEvent.preventDefault();
          latestDragPointer = moveEvent;
          if (!dragFrame) dragFrame = window.requestAnimationFrame(() => {
            dragFrame = 0;
            updateDrag(latestDragPointer);
          });
        };

        const onEnd = (endEvent) => {
          if (endEvent.pointerId !== pointerId) return;
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onEnd);
          window.removeEventListener('pointercancel', onEnd);
          if (dragFrame) window.cancelAnimationFrame(dragFrame);
          try {
            if (button.hasPointerCapture(pointerId)) button.releasePointerCapture(pointerId);
          } catch {
            // Pointer capture can be released before pointerup.
          }
          if (!dragging) return;
          if (endEvent.type !== 'pointercancel') {
            dragRect = canvas.getBoundingClientRect();
            dragGeometry = orbitGeometry(canvas);
            updateDrag(endEvent);
          }
          suppressMaterialClickUntil = performance.now() + 320;
          const shouldAdd = overCanvas && endEvent.type !== 'pointercancel';
          const insertAt = materialDropIndex;
          clearMaterialDrag();
          if (shouldAdd) addMaterial(button, insertAt, { x: endEvent.clientX, y: endEvent.clientY });
        };

        try { button.setPointerCapture(pointerId); } catch { /* Pointer capture is an enhancement. */ }
        window.addEventListener('pointermove', onMove, { passive: false });
        window.addEventListener('pointerup', onEnd);
        window.addEventListener('pointercancel', onEnd);
      });
    };

    const bindMaterialButtons = () => {
      materialButtons = [...root.querySelectorAll('[data-material]')];
      materialButtons.forEach(bindMaterialButton);
    };

    const activeCategory = () => (
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true')?.dataset.categoryTab || 'beads'
    );

    const filteredCatalogItems = (category) => {
      const query = String(materialSearch?.value || '').trim().toLocaleLowerCase();
      let candidates;
      if (query) {
        candidates = catalogItems.filter((material) => material.searchText.includes(query));
      } else if (category === 'accents') {
        candidates = catalogItems.filter((material) => material.kind === 'spacer');
      } else if (category === 'charms') {
        candidates = catalogItems.filter((material) => material.kind === 'charm');
      } else {
        candidates = catalogItems.filter((material) => material.kind === 'bead');
        if (activeMaterialType === 'used') {
          const usedKeys = new Set(state.items.map((item) => item.key));
          candidates = candidates.filter((material) => usedKeys.has(material.key));
        } else if (activeMaterialType !== 'all') {
          candidates = candidates.filter((material) => material.materialType === activeMaterialType);
        }
      }
      return candidates;
    };

    const updateMaterialUsage = () => {
      const counts = new Map();
      state.items.forEach((item) => counts.set(item.key, (counts.get(item.key) || 0) + 1));
      materialButtons.forEach((button) => {
        const count = counts.get(button.dataset.key) || 0;
        const stock = number(button.dataset.stock);
        const exhausted = button.dataset.kind !== 'cord' && stock > 0 && count >= stock;
        const shouldDisable = !catalogReady || exhausted;
        if (materialUsageCache.get(button) === count && button.disabled === shouldDisable) return;
        materialUsageCache.set(button, count);
        const badge = button.querySelector('[data-material-use]');
        button.classList.toggle('is-used', count > 0);
        button.disabled = shouldDisable;
        button.classList.toggle('is-exhausted', exhausted);
        syncMaterialDraggable(button);
        if (!badge) return;
        badge.hidden = count < 1;
        badge.textContent = count > 0 ? labels.used.replace('__COUNT__', String(count)) : '';
      });
      if (catalogReady && activeMaterialType === 'used' && !catalogRendering) renderCatalog(activeCategory());
    };

    const renderCatalog = (category = activeCategory()) => {
      if (!catalogReady || category === 'cord') {
        if (materialTypes) materialTypes.hidden = true;
        return;
      }
      const panel = catalogPanels.get(category);
      if (!panel) return;
      catalogRendering = true;
      const matches = filteredCatalogItems(category);
      const existing = new Map([...panel.querySelectorAll('[data-material]')].map((button) => [button.dataset.key, button]));
      const nextKeys = new Set(matches.map((material) => material.key));
      existing.forEach((button, key) => {
        if (nextKeys.has(key)) return;
        button.querySelectorAll('img[data-catalog-src]').forEach((image) => catalogImageObserver?.unobserve(image));
        button.remove();
      });
      const activeCategoryName = activeCategory();
      const isVisibleCategory = category === activeCategoryName;
      if (!panel.dataset.melImagesScrollBound) {
        panel.addEventListener('scroll', () => ensureVisibleCatalogImages(panel), { passive: true });
        panel.dataset.melImagesScrollBound = 'true';
      }
      matches.forEach((material, index) => {
        // Only the visible material panel gets its first row eagerly. Hidden
        // category panels stay image-free until the shopper opens them; this
        // avoids downloading three 8-card batches during a phone cold load.
        // The desktop material pane shows up to five rows at once. Eagerly
        // resolve that visible window so evidence captures never contain a
        // placeholder row; phones keep the smaller first-screen budget.
        const priority = isVisibleCategory && index < (window.innerWidth >= 768 ? 20 : 8);
        const button = existing.get(material.key) || createCatalogCard(material, priority);
        prepareCatalogImage(button, material, priority);
        const nextNode = panel.children[index];
        if (nextNode !== button) panel.insertBefore(button, nextNode || null);
      });
      bindMaterialButtons();
      updateMaterialUsage();
      if (materialEmpty) materialEmpty.hidden = matches.length > 0;
      if (materialTypes) materialTypes.hidden = category !== 'beads' || Boolean(String(materialSearch?.value || '').trim());
      if (catalogSummary) {
        catalogSummary.textContent = messages.catalogLoaded.replace('__COUNT__', String(matches.length));
      }
      catalogRendering = false;
      requestAnimationFrame(() => ensureVisibleCatalogImages(panel));
    };

    const renderMaterialTypes = () => {
      if (!materialTypes) return;
      materialTypes.querySelectorAll('[data-generated-material-type]').forEach((button) => button.remove());
      const summary = catalogSummary;
      catalogTypes
        .filter((type) => !['Spacer', 'Pendant'].includes(type))
        .forEach((type) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.dataset.materialType = type;
          button.dataset.generatedMaterialType = '';
          button.setAttribute('aria-pressed', String(type === activeMaterialType));
          button.textContent = type;
          materialTypes.insertBefore(button, summary || null);
        });
      materialTypes.hidden = activeCategory() !== 'beads';
    };

    const getCatalogIssues = () => {
      const byKey = new Map(catalogItems.map((material) => [material.key, material]));
      const useCount = new Map();
      state.items.forEach((item) => useCount.set(item.key, (useCount.get(item.key) || 0) + 1));
      return state.items.filter((item) => {
        const current = byKey.get(item.key);
        if (!current || current.stock < useCount.get(item.key)) return true;
        return item.catalogState === 'unavailable' || item.catalogState === 'updated';
      });
    };

    const reconcileCatalogItems = () => {
      const byKey = new Map(catalogItems.map((material) => [material.key, material]));
      const useCount = new Map();
      state.items.forEach((item) => useCount.set(item.key, (useCount.get(item.key) || 0) + 1));
      const updated = [];
      const unavailable = [];
      const nextItems = state.items.map((item) => {
        const current = byKey.get(item.key);
        if (!current || current.stock < useCount.get(item.key)) {
          const reason = !current ? 'unavailable' : 'out-of-stock';
          unavailable.push({ item, reason });
          return { ...item, catalogState: 'unavailable', catalogUnavailableReason: reason };
        }
        const changed = Math.round(number(item.price) * 100) !== Math.round(current.price * 100)
          || Math.abs(number(item.size) - current.size) > 0.01
          || Math.abs(occupiedWidth(item) - current.fitWidth) > 0.01
          || Math.abs(number(item.width) - current.width) > 0.01
          || Math.abs(number(item.height) - current.height) > 0.01
          || item.name !== current.name
          || item.image !== current.image
          || item.thumbnail !== current.thumbnail;
        if (changed) updated.push({
          id: item.id,
          name: current.name,
          beforePrice: number(item.price),
          afterPrice: number(current.price)
        });
        const refreshed = itemFromMaterial(current);
        refreshed.id = item.id;
        if (changed) refreshed.catalogState = 'updated';
        return refreshed;
      });
      state.items = nextItems;
      catalogReconciliation = { updated, unavailable };
      if (updated.length || unavailable.length) {
        state.requestId = '';
        scheduleDraft();
      }
    };

    const updateReconciliationBanner = () => {
      const unavailable = catalogReconciliation.unavailable;
      if (!catalogReconcile) return;
      const hasUnavailable = unavailable.length > 0;
      catalogReconcile.hidden = !hasUnavailable;
      if (!hasUnavailable) return;
      const text = (messages.catalogUnavailable || messages.catalogStale || 'Some saved materials are no longer available.').replace('__COUNT__', String(unavailable.length));
      if (catalogReconcileText) catalogReconcileText.textContent = text;
      if (catalogReconcileReplace) catalogReconcileReplace.hidden = false;
      if (catalogReconcileRemove) {
        catalogReconcileRemove.hidden = false;
        catalogReconcileRemove.textContent = (messages.catalogRemoveUnavailable || 'Remove unavailable (__COUNT__)').replace('__COUNT__', String(unavailable.length));
      }
    };

    const showCatalogUpdateToast = () => {
      if (!catalogUpdateToast || !catalogReconciliation.updated.length) return;
      const first = catalogReconciliation.updated[0];
      const priceChanged = Math.round(first.beforePrice * 100) !== Math.round(first.afterPrice * 100);
      const detail = priceChanged
        ? `${currency.format(first.beforePrice)} → ${currency.format(first.afterPrice)}`
        : `${first.name} · ${messages.catalogUpdatedInline || 'updated to today\'s library'}`;
      catalogUpdateToast.textContent = priceChanged
        ? `${messages.catalogUpdatedInline || 'Prices updated to today\'s library'} · ${detail}`
        : detail;
      catalogUpdateToast.hidden = false;
      window.clearTimeout(catalogUpdateTimer);
      catalogUpdateTimer = window.setTimeout(() => { catalogUpdateToast.hidden = true; }, 7000);
    };

    const updateCatalogNotice = (issues = []) => {
      const needsNotice = !catalogReady || catalogLoadFailed;
      const text = catalogLoadFailed
        ? `${messages.catalogError}${catalogFailureReason ? ' ' + catalogFailureReason : ''}`
        : messages.catalogLoading;
      if (catalogNotice) catalogNotice.hidden = !needsNotice;
      if (catalogNoticeText) catalogNoticeText.textContent = text;
      if (commerceCatalogNote) commerceCatalogNote.hidden = !needsNotice && !issues.length;
      if (commerceCatalogText) commerceCatalogText.textContent = catalogReady || catalogLoadFailed
        ? (issues.length ? (messages.catalogUnavailable || messages.catalogStale) : text) : messages.catalogCheckoutLoading;
      catalogRetryButtons.forEach((button) => {
        button.hidden = !catalogLoadFailed;
        button.disabled = state.commerceBusy;
      });
    };

    const applyCatalog = (payload, fromCache = false) => {
      const normalized = normalizeCatalogPayload(payload);
      if (!normalized.items.length) throw new Error('Material library was empty');
      catalogTypes = normalized.types;
      catalogItems = normalized.items;
      root.dataset.catalogVisibleCount = String(catalogItems.length);
      root.dataset.catalogHiddenCount = String(normalized.hiddenCount || 0);
      root.dataset.catalogTypes = catalogTypes.join('|');
      catalogReady = true;
      reconcileCatalogItems();
      warmCurrentDesignImages();
      renderMaterialTypes();
      ['beads', 'accents', 'charms'].forEach(renderCatalog);
      renderCatalog(activeCategory());
      root.dataset.catalogState = 'ready';
      root.dataset.catalogSource = fromCache ? 'cache' : 'live';
      if (!root.dataset.catalogPaintMs) root.dataset.catalogPaintMs = String(Math.round(performance.now()));
      if (fromCache) root.dataset.catalogCachePaintMs = String(Math.round(performance.now()));
      updateReconciliationBanner();
      showCatalogUpdateToast();
      if (applySharedDesign()) {
        fitOrbitZoom();
        render();
        announce('Shared design loaded');
      }
    };

    const loadMaterialCatalog = async () => {
      const endpoint = root.dataset.materialsEndpoint;
      catalogLoadFailed = false;
      catalogFailureReason = '';
      if (!catalogReady) {
        try {
          const cached = JSON.parse(window.localStorage.getItem(catalogStorageKey));
          const storedFresh = Number(cached?.savedAt) > Date.now() - 6 * 60 * 60 * 1000;
          if (cached?.endpoint === endpoint && cached.currency === (root.dataset.currencyCode || 'USD') && storedFresh) {
            applyCatalog(cached.payload, true);
            usingStoredCatalog = true;
          }
        } catch {}
      }
      root.dataset.catalogState = catalogReady ? 'ready' : 'loading';
      updateMaterialUsage();
      updateCatalogNotice();
      updateActionState();
      if (!endpoint) {
        catalogLoadFailed = true;
        root.dataset.catalogState = 'error';
        updateCatalogNotice();
        return;
      }
      if (catalogSummary && !catalogReady) catalogSummary.textContent = messages.catalogLoading;
      catalogRetryButtons.forEach((button) => { button.disabled = true; });
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const earlyRequest = window.melDesignerCatalogRequest;
        window.melDesignerCatalogRequest = null;
        const payload = earlyRequest ? await earlyRequest : await fetch(endpoint, {
          credentials: 'omit', headers: { Accept: 'application/json' }, signal: controller.signal
        }).then((response) => {
          if (!response.ok) throw new Error(`Material library request failed (${response.status})`);
          return response.json();
        });
        const previousPrices = new Map(catalogItems.map((item) => [item.key, `${item.price}:${item.stock}`]));
        applyCatalog(payload);
        usingStoredCatalog = false;
        catalogRetryCount = 0;
        const changed = previousPrices.size && catalogItems.some((item) => previousPrices.has(item.key) && previousPrices.get(item.key) !== `${item.price}:${item.stock}`);
        try { window.localStorage.setItem(catalogStorageKey, JSON.stringify({ endpoint, currency: root.dataset.currencyCode || 'USD', savedAt: Date.now(), payload })); } catch {}
        announce(changed ? root.dataset.messageCatalogUpdated || messages.catalogStale
          : messages.catalogLoaded.replace('__COUNT__', String(catalogItems.length)));
      } catch (error) {
        // Retry quietly: a stored, verified catalog keeps checkout open meanwhile.
        if (catalogRetryCount < 3 && (usingStoredCatalog || !catalogReady)) {
          catalogRetryCount += 1;
          window.setTimeout(loadMaterialCatalog, 3000 * catalogRetryCount);
          return;
        }
        catalogLoadFailed = true;
        catalogFailureReason = error?.name === 'AbortError' ? 'The request timed out.'
          : String(error?.message || 'The request failed.').slice(0, 180);
        if (!catalogReady) {
          root.dataset.catalogState = 'error';
          if (materialTypes) materialTypes.hidden = true;
          if (catalogSummary) catalogSummary.textContent = messages.catalogError;
        } else announce(messages.catalogError);
      } finally {
        window.clearTimeout(timeout);
        updateActionState();
      }
    };

    const pulseMaterialButton = (button) => {
      if (!button?.isConnected) return;
      const previousTimer = materialFeedbackTimers.get(button);
      if (previousTimer) window.clearTimeout(previousTimer);
      button.classList.add('is-adding');
      button.dataset.feedback = '+1';
      if (coarsePointer.matches && typeof navigator.vibrate === 'function') navigator.vibrate(10);
      materialFeedbackTimers.set(button, window.setTimeout(() => {
        button.classList.remove('is-adding');
        delete button.dataset.feedback;
        materialFeedbackTimers.delete(button);
      }, 260));
    };

    const rememberMaterialOrigin = (button, itemId) => {
      if (!button || reducedMotion) return;
      const source = button.querySelector('.mel-designer__swatch, .mel-designer__cord-swatch') || button;
      const rect = source.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      pendingArrivalOrigins.set(itemId, {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    };

    const animateMaterialToOrbit = (itemId, canvasRect) => {
      const target = layer.querySelector(`[data-bead-id="${CSS.escape(itemId)}"]`);
      const revealTarget = () => {
        pendingFlightIds.delete(itemId);
        pendingArrivalOrigins.delete(itemId);
        target?.classList.remove('is-flight-pending', 'is-entering');
      };

      if (!target || reducedMotion || !canvasRect.width || typeof target.animate !== 'function') {
        revealTarget();
        return;
      }
      const position = beadPositions.get(target);
      if (!position) {
        revealTarget();
        return;
      }

      const targetCenterX = canvasRect.left + canvasRect.width / 2 + position.x;
      const targetCenterY = canvasRect.top + canvasRect.height / 2 + orbitCenterOffsetY() + position.y;
      const savedOrigin = pendingArrivalOrigins.get(itemId);
      const originCenterX = savedOrigin?.x ?? (canvasRect.left - Math.min(72, canvasRect.width * 0.16));
      const originCenterY = savedOrigin?.y ?? (canvasRect.top + canvasRect.height / 2);
      const offsetX = originCenterX - targetCenterX;
      const offsetY = originCenterY - targetCenterY;
      const arcY = Math.min(44, canvasRect.height * 0.11);

      pendingFlightIds.delete(itemId);
      pendingArrivalOrigins.delete(itemId);
      target.classList.remove('is-flight-pending', 'is-entering', 'is-arriving');
      const animation = target.animate([
        {
          opacity: 0,
          offset: 0,
          transform: `translate(calc(-50% + ${offsetX}px), calc(-50% + ${offsetY}px)) scale(0.82) rotate(-8deg)`
        },
        {
          opacity: 1,
          offset: 0.58,
          transform: `translate(calc(-50% + ${offsetX * 0.42}px), calc(-50% + ${offsetY * 0.42 - arcY}px)) scale(1.06) rotate(3deg)`
        },
        {
          opacity: 1,
          offset: 1,
          transform: 'translate(-50%, -50%) scale(1) rotate(0deg)'
        }
      ], {
        duration: 240,
        easing: 'cubic-bezier(0.22, 0.78, 0.24, 1)',
        fill: 'forwards'
      });
      animation.finished.catch(() => null).then(() => {
        animation.cancel();
        revealTarget();
      });
    };

    const startMaterialArrivals = () => {
      arrivalFrame = 0;
      const canvasRect = canvas.getBoundingClientRect();
      [...pendingFlightIds].forEach((itemId) => animateMaterialToOrbit(itemId, canvasRect));
    };

    const queueMaterialArrival = (button) => {
      pulseMaterialButton(button);
      if (!reducedMotion && !arrivalFrame) arrivalFrame = window.requestAnimationFrame(startMaterialArrivals);
    };

    const addMaterial = (button, insertAt = null, origin = null) => {
      if (button.dataset.kind === 'cord') {
        remember();
        state.cord = {
          key: button.dataset.key,
          name: button.dataset.name,
          visual: button.dataset.visual,
          price: number(button.dataset.price)
        };
        announce(messages.added, button.dataset.name);
        render();
        return;
      }

      const stock = number(button.dataset.stock);
      const usedCount = state.items.filter((item) => item.key === button.dataset.key).length;
      if (stock > 0 && usedCount >= stock) {
        announce(messages.full, button.dataset.name);
        updateMaterialUsage();
        return;
      }

      const material = itemFromButton(button);
      let placeAt = Number.isInteger(insertAt) ? insertAt : null;
      // A charm needs its own gap between two beads. Ask the customer to add a bead rather than stacking it.
      if (isPendantItem(material) && !replacingId) {
        if (placeAt === null) placeAt = tapCharmCandidate(state.items)?.index ?? state.items.length;
        const next = [...state.items.slice(0, placeAt), material, ...state.items.slice(placeAt)];
        if (!charmsFit(next)) {
          announce('Add another bead to hang another charm.');
          flash('Add another bead to hang another charm.');
          return;
        }
      }
      if (replacingAllKey) {
        const matches = state.items.filter((item) => item.key === replacingAllKey);
        if (material.stock > 0 && matches.length > material.stock) {
          announce(messages.full, material.name);
          return;
        }
        const replacementWidth = physicalOccupation(material) * matches.length;
        const retainedWidth = state.items.reduce((sum, item) => item.key === replacingAllKey ? sum : sum + physicalOccupation(item), 0);
        if (!matches.length || retainedWidth + replacementWidth > maximumDesignMm) return showCapacityLimit();
        const previous = snapshot();
        state.items = state.items.map((item) => item.key === replacingAllKey ? { ...material, id: item.id } : item);
        remember(previous);
        state.selectedId = null;
        replacingAllKey = null;
        root.classList.remove('is-replacing-all');
        setPanelMode?.('collapsed');
        render();
        announce('Replaced all matching pieces.', material.name);
        return;
      }
      const replaceIndex = state.items.findIndex((item) => item.id === replacingId);
      if (replaceIndex >= 0) {
        if (totals().usedMm - physicalOccupation(state.items[replaceIndex]) + physicalOccupation(material) > maximumDesignMm) return showCapacityLimit();
        remember();
        const replacedId = state.items[replaceIndex].id;
        material.id = state.items[replaceIndex].id;
        state.items[replaceIndex] = material;
        catalogReconciliation.unavailable = catalogReconciliation.unavailable.filter(({ item }) => item.id !== replacedId);
        state.selectedId = material.id;
        replacingId = null;
        root.classList.remove('is-replacing');
        setPanelMode?.('collapsed');
        updateReconciliationBanner();
        render();
        announce('Replaced __NAME__ in your design.', material.name);
        return;
      }
      const current = totals();
      const requiredMm = current.usedMm + physicalOccupation(material);
      if (requiredMm > maximumDesignMm || state.items.length >= 40) {
        showCapacityLimit();
        return;
      }
      remember();
      if (!reducedMotion) pendingFlightIds.add(material.id);
      if (origin && !reducedMotion) pendingArrivalOrigins.set(material.id, origin);
      else rememberMaterialOrigin(button, material.id);
      const insertionIndex = placeAt !== null ? Math.max(0, Math.min(state.items.length, placeAt)) : state.items.length;
      state.items.splice(insertionIndex, 0, material);
      fitOrbitZoom();
      // Adding a bead gives feedback without opening the selection toolbar.
      // The toolbar appears only after the customer explicitly taps a bead.
      state.selectedId = null;
      announce(messages.added, material.name);
      scheduleDraft();
      updatePreviewThumbnail();
      requestRender();
      queueMaterialArrival(button, material.id);
    };

    const inspireDesign = () => {
      const beadChoices = catalogReady
        ? catalogItems
          .filter((material) => material.kind === 'bead')
          .map((material) => ({
            key: material.key,
            size: material.size,
            fitWidth: material.fitWidth,
            width: material.width,
            height: material.height,
            stock: material.stock,
            createItem: () => itemFromMaterial(material)
          }))
        : materialButtons
          .filter((button) => button.dataset.kind === 'bead')
          .map((button) => ({
            key: button.dataset.key,
            size: number(button.dataset.size),
            fitWidth: positiveNumber(button.dataset.fitWidth, button.dataset.size),
            width: positiveNumber(button.dataset.width, button.dataset.size),
            height: positiveNumber(button.dataset.height, button.dataset.width, button.dataset.size),
            stock: number(button.dataset.stock),
            createItem: () => itemFromButton(button)
          }));
      if (!beadChoices.length) return;
      remember();
      const preset = currentPreset();
      const desiredFitMm = selectedWristMm(preset) + preferredEaseMm;
      const inspiredItems = [];
      const inspiredCounts = new Map();
      while (inspiredItems.length < 40) {
        const currentMeasurement = measureItems(inspiredItems);
        if (fitStatusFor(currentMeasurement.usedMm, preset) === 'good') break;
        const candidates = beadChoices
          .filter((choice) => choice.stock < 1 || (inspiredCounts.get(choice.key) || 0) < choice.stock)
          .map((choice) => ({ choice, measurement: measureItems([
            ...inspiredItems,
            {
              size: choice.size,
              fitWidth: choice.fitWidth,
              width: choice.width,
              height: choice.height
            }
          ]) }))
          .filter(({ measurement }) => measurement.usedMm <= maximumDesignMm);
        if (!candidates.length) break;
        const goodCandidates = candidates.filter(
          ({ measurement }) => fitStatusFor(measurement.usedMm, preset) === 'good'
        );
        const choice = goodCandidates.length
          ? goodCandidates[Math.floor(Math.random() * goodCandidates.length)].choice
          : currentMeasurement.usedMm >= desiredFitMm - 20
            ? candidates.sort(
              (first, second) =>
                Math.abs(first.measurement.usedMm - desiredFitMm) -
                Math.abs(second.measurement.usedMm - desiredFitMm)
            )[0].choice
            : candidates[Math.floor(Math.random() * candidates.length)].choice;
        inspiredItems.push(choice.createItem());
        inspiredCounts.set(choice.key, (inspiredCounts.get(choice.key) || 0) + 1);
      }
      state.items = inspiredItems;
      fitOrbitZoom();
      state.selectedId = null;
      announce(messages.inspired);
      render();
    };

    const removeSelected = () => {
      const index = state.items.findIndex((item) => item.id === state.selectedId);
      if (index < 0) return;
      remember();
      const [removed] = state.items.splice(index, 1);
      state.selectedId = null;
      announce(messages.removed, removed.name);
      render();
    };

    const fillToFit = (preferredKey = '') => {
      const counts = new Map();
      state.items.filter((item) => item.kind === 'bead').forEach((item) => counts.set(item.key, (counts.get(item.key) || 0) + 1));
      const mainKey = preferredKey || [...counts].sort((a, b) => b[1] - a[1])[0]?.[0];
      const material = catalogItems.find((item) => item.key === mainKey)
        || catalogItems.find((item) => item.name === 'Clear White Crystal' && item.size === 8);
      if (!material) return;
      const previous = snapshot();
      let changed = false;
      while (!['snug', 'good', 'loose', 'tooLoose'].includes(totals().fitStatus) && state.items.length < 40) {
        if (totals().usedMm + physicalOccupation(material) > maximumDesignMm || state.items.filter((item) => item.key === material.key).length >= material.stock) break;
        const next = itemFromMaterial(material);
        state.items.push(next); changed = true;
        // A snug result can be improved to Good by one more main bead.
        if (totals().fitStatus === 'snug' && totals().usedMm + physicalOccupation(material) <= maximumDesignMm && state.items.filter((item) => item.key === material.key).length < material.stock) state.items.push(itemFromMaterial(material));
      }
      if (changed) { remember(previous); state.selectedId = null; render(); announce('Filled to the recommended fit. Undo is available.'); }
    };

    const moveSelected = (offset) => {
      const index = state.items.findIndex((item) => item.id === state.selectedId);
      const target = index + offset;
      if (index < 0 || target < 0 || target >= state.items.length) return;
      const moved = [...state.items];
      const [item] = moved.splice(index, 1);
      moved.splice(target, 0, item);
      if (!charmsFit(moved)) {
        announce('Add another bead to hang another charm.');
        flash('That spot already has a charm.');
        return;
      }
      remember();
      state.items.splice(index, 1);
      state.items.splice(target, 0, item);
      announce(messages.moved, item.name);
      render();
    };

    const repeatSelectedPattern = () => {
      const index = state.items.findIndex((item) => item.id === state.selectedId);
      if (index < 0) return announce('Select a piece to repeat.');
      const selectedKey = state.items[index].key;
      let start = index;
      let end = index + 1;
      while (start > 0 && state.items[start - 1].key === selectedKey) start -= 1;
      while (end < state.items.length && state.items[end].key === selectedKey) end += 1;
      const pattern = state.items.slice(start, end);
      const additions = pattern.map((item) => ({ ...item, id: `${item.key}-${Date.now().toString(36)}-${itemSequence += 1}` }));
      const nextWidth = additions.reduce((sum, item) => sum + physicalOccupation(item), 0);
      const nextItems = [...state.items.slice(0, end), ...additions, ...state.items.slice(end)];
      if (!additions.length || state.items.length + additions.length > 40 || totals().usedMm + nextWidth > maximumDesignMm) {
        announce(messages.maxSize || messages.full);
        return;
      }
      if (!charmsFit(nextItems)) {
        announce('Add another bead to hang another charm.');
        flash('Add another bead to hang another charm.');
        return;
      }
      remember();
      state.items.splice(end, 0, ...additions);
      state.selectedId = additions[0].id;
      fitOrbitZoom();
      render();
      announce('Pattern repeated. Undo is available.');
    };

    const mirrorDesign = () => {
      if (state.items.length < 2) return announce('Add at least two pieces to mirror the pattern.');
      remember();
      state.items.reverse();
      state.selectedId = null;
      render();
      announce('Pattern mirrored. Undo is available.');
    };

    const selectBead = (itemId) => {
      state.selectedId = itemId;
      layer.querySelectorAll('[data-bead-id]').forEach((node) => {
        node.classList.toggle('is-selected', node.dataset.beadId === itemId);
      });
      updateActionState();
    };

    const bindBeads = () => {
      const nearestBead = (clientX, clientY) => {
        const rect = canvas.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2 + orbitCenterOffsetY();
        let nearest, distance = Infinity;
        layer.querySelectorAll('[data-bead-id]').forEach((node) => {
          const position = beadPositions.get(node);
          if (!position) return;
          const next = Math.hypot(clientX - centerX - position.x, clientY - centerY - position.y);
          if (next < distance) { nearest = node; distance = next; }
        });
        return nearest;
      };
      layer.querySelectorAll('[data-bead-id]').forEach((bead) => {
        if (bead.dataset.bound === 'true') return;
        bead.dataset.bound = 'true';
        let suppressClick = false;
        let pointerSelectedId = null;

        bead.addEventListener('click', () => {
          if (suppressClick) {
            suppressClick = false;
            pointerSelectedId = null;
            return;
          }
          selectBead(pointerSelectedId || bead.dataset.beadId);
          pointerSelectedId = null;
        });

        bead.addEventListener('keydown', (event) => {
          if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            state.selectedId = bead.dataset.beadId;
            removeSelected();
          }
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            state.selectedId = bead.dataset.beadId;
            moveSelected(event.key === 'ArrowLeft' ? -1 : 1);
          }
        });

        bead.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          // Touch targets may overlap in dense strands. Route the original
          // pointer to the nearest visible bead, without redispatching input.
          const bead = nearestBead(event.clientX, event.clientY) || event.currentTarget;
          const itemId = bead.dataset.beadId;
          pointerSelectedId = itemId;
          const startIndex = state.items.findIndex((item) => item.id === itemId);
          if (startIndex < 0) return;
          const activePointerId = event.pointerId;
          const rect = canvas.getBoundingClientRect();
          const centerOffsetY = orbitCenterOffsetY();
          const startPointer = { x: event.clientX, y: event.clientY };
          let latestPointer = event;
          let previewOrder = [...state.items];
          let previewIndex = startIndex;
          let deleteArmed = false;
          let animationFrame = 0;
          let dragging = false;
          let holdReady = event.pointerType !== 'touch';
          let holdTimer = 0;
          suppressClick = false;
          selectBead(itemId);
          if (!holdReady) holdTimer = window.setTimeout(() => { holdReady = true; }, 300);

          const paintDrag = () => {
            animationFrame = 0;
            const deltaX = latestPointer.clientX - startPointer.x;
            const deltaY = latestPointer.clientY - startPointer.y;
            if (Math.hypot(deltaX, deltaY) > 4) suppressClick = true;
            bead.style.setProperty('--drag-x', `${deltaX}px`);
            bead.style.setProperty('--drag-y', `${deltaY}px`);

            const x = latestPointer.clientX - rect.left - rect.width / 2;
            const y = latestPointer.clientY - rect.top - rect.height / 2 - centerOffsetY;
            let angle = Math.atan2(y, x) + Math.PI / 2 - state.rotationRad;
            while (angle < 0) angle += Math.PI * 2;
            while (angle >= Math.PI * 2) angle -= Math.PI * 2;
            // The drop is the candidate position nearest the pointer, counted in the list without the dragged piece.
            const draggedItem = state.items.find((item) => item.id === itemId);
            const candidates = dropCandidates(state.items, isPendantItem(draggedItem), itemId);
            const pointerFraction = angle / (Math.PI * 2);
            const nearest = candidates.reduce((best, candidate) => (
              !best || fractionGap(candidate.fraction, pointerFraction) < fractionGap(best.fraction, pointerFraction) ? candidate : best
            ), null);
            const nextIndex = nearest ? nearest.index : state.items.length - 1;
            if (nextIndex !== previewIndex) {
              const [movingItem] = previewOrder.splice(previewIndex, 1);
              previewOrder.splice(nextIndex, 0, movingItem);
              previewIndex = nextIndex;
              positionExistingOrbit(previewOrder, itemId);
            }
            deleteArmed = latestPointer.clientY > rect.bottom - Math.max(52, rect.height * 0.13);
            canvas.classList.toggle('is-delete-armed', deleteArmed);
          };

          const onMove = (moveEvent) => {
            if (moveEvent.pointerId !== activePointerId) return;
            latestPointer = moveEvent;
            if (!holdReady) return;
            if (!dragging && Math.hypot(moveEvent.clientX - startPointer.x, moveEvent.clientY - startPointer.y) <= 6) return;
            if (!dragging) {
              dragging = true;
              moveEvent.preventDefault();
              try { bead.setPointerCapture(activePointerId); } catch { /* Pointer capture is progressive enhancement. */ }
              bead.classList.add('is-dragging');
              canvas.classList.add('is-dragging');
            }
            if (!animationFrame) animationFrame = window.requestAnimationFrame(paintDrag);
          };

          const onEnd = (endEvent) => {
            if (endEvent.pointerId !== activePointerId) return;
            if (holdTimer) window.clearTimeout(holdTimer);
            if (!dragging) {
              window.removeEventListener('pointermove', onMove);
              window.removeEventListener('pointerup', onEnd);
              window.removeEventListener('pointercancel', onEnd);
              return;
            }
            if (animationFrame) {
              window.cancelAnimationFrame(animationFrame);
              paintDrag();
            }
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onEnd);
            window.removeEventListener('pointercancel', onEnd);
            try {
              if (bead.hasPointerCapture(activePointerId)) bead.releasePointerCapture(activePointerId);
            } catch {
              // Pointer capture can be released by the browser before pointerup.
            }
            bead.classList.remove('is-dragging');
            bead.style.removeProperty('--drag-x');
            bead.style.removeProperty('--drag-y');
            canvas.classList.remove('is-dragging', 'is-delete-armed');
            if (endEvent.type === 'pointercancel') {
              render();
              return;
            }
            if (deleteArmed) {
              removeSelected();
              return;
            }
            if (previewIndex !== startIndex) {
              remember();
              state.items = previewOrder;
              const movedItem = state.items[previewIndex];
              announce(messages.moved, movedItem.name);
            }
            render();
          };

          window.addEventListener('pointermove', onMove, { passive: false });
          window.addEventListener('pointerup', onEnd);
          window.addEventListener('pointercancel', onEnd);
        });
      });
    };

    const bindCanvasRotation = () => {
      let activeRotation = null;
      let animationFrame = 0;
      let latestEvent = null;

      const pointerAngle = (event, rect) => Math.atan2(
        event.clientY - rect.top - rect.height / 2 - orbitCenterOffsetY(),
        event.clientX - rect.left - rect.width / 2
      );

      const paintRotation = () => {
        animationFrame = 0;
        if (!activeRotation || !latestEvent) return;
        state.rotationRad = activeRotation.startRotation + pointerAngle(latestEvent, activeRotation.rect) - activeRotation.startAngle;
        positionExistingOrbit(state.items);
      };

      canvas.addEventListener('pointerdown', (event) => {
        if (event.target.closest('[data-bead-id]')) return;
        if (event.target.closest('button, a, input, select, [role="tab"], [role="button"], label')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const rect = canvas.getBoundingClientRect();
        activeRotation = {
          pointerId: event.pointerId,
          rect,
          startAngle: pointerAngle(event, rect),
          startRotation: state.rotationRad,
          startX: event.clientX,
          startY: event.clientY,
          started: false
        };
        latestEvent = event;
      });

      canvas.addEventListener('pointermove', (event) => {
        if (!activeRotation || activeRotation.pointerId !== event.pointerId) return;
        latestEvent = event;
        if (!activeRotation.started && Math.hypot(event.clientX - activeRotation.startX, event.clientY - activeRotation.startY) <= 6) return;
        if (!activeRotation.started) {
          activeRotation.started = true;
          event.preventDefault();
          try { canvas.setPointerCapture(event.pointerId); } catch { /* Pointer capture is progressive enhancement. */ }
          canvas.classList.add('is-rotating');
        }
        if (!animationFrame) animationFrame = window.requestAnimationFrame(paintRotation);
      });

      const finishRotation = (event) => {
        if (!activeRotation || activeRotation.pointerId !== event.pointerId) return;
        latestEvent = event;
        if (!activeRotation.started) {
          activeRotation = null;
          latestEvent = null;
          return;
        }
        if (animationFrame) {
          window.cancelAnimationFrame(animationFrame);
          paintRotation();
        }
        activeRotation = null;
        latestEvent = null;
        canvas.classList.remove('is-rotating');
        renderOrbit(canvas, layer, true);
      };

      canvas.addEventListener('pointerup', finishRotation);
      canvas.addEventListener('pointercancel', finishRotation);
      canvas.addEventListener('click', (event) => {
        if (event.target === canvas) selectBead(null);
      });
      canvas.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || event.target !== canvas) return;
        event.preventDefault();
        state.rotationRad += event.key === 'ArrowLeft' ? -Math.PI / 18 : Math.PI / 18;
        renderOrbit(canvas, layer, true);
      });
    };

    // Proxy first: it is same-origin and cached, so it still works where the image
    // host is slow or unreachable. The direct image host is the last resort.
    const canvasImageSources = (source) => {
      try {
        const sourceUrl = new URL(String(source || ''), window.location.origin);
        if (commerceAssetEndpoint && sourceUrl.protocol === 'https:' && sourceUrl.hostname === 'assets.myastris.com') {
          const proxyUrl = new URL(commerceAssetEndpoint, window.location.origin);
          proxyUrl.searchParams.set('source', sourceUrl.href);
          return [proxyUrl.href, sourceUrl.href];
        }
        return [sourceUrl.href];
      } catch {
        return [String(source || '')];
      }
    };

    const fetchCanvasImage = async (imageSource) => {
      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), 7000);
      try {
        const response = await fetch(imageSource, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`Image request failed (${response.status})`);
        return await response.blob();
      } finally {
        window.clearTimeout(timer);
      }
    };

    const loadCanvasImage = (source) => {
      if (canvasImageCache.has(source)) return canvasImageCache.get(source);
      const lastFailure = canvasImageFailures.get(source) || 0;
      if (Date.now() - lastFailure < 3_000) {
        return Promise.reject(new Error('Material preview image is temporarily unavailable.'));
      }
      const pending = (async () => {
        let lastError;
        for (const imageSource of canvasImageSources(source)) {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            try {
              const blob = await fetchCanvasImage(attempt === 0 ? imageSource : withRetryParam(imageSource, attempt));
              const objectUrl = URL.createObjectURL(blob);
              try {
                const image = new Image();
                image.decoding = 'async';
                await new Promise((resolve, reject) => {
                  image.onload = resolve;
                  image.onerror = reject;
                  image.src = objectUrl;
                });
                return image;
              } finally {
                URL.revokeObjectURL(objectUrl);
              }
            } catch (error) {
              lastError = error;
            }
          }
        }
        throw lastError;
      })().catch((error) => {
        canvasImageCache.delete(source);
        canvasImageFailures.set(source, Date.now());
        throw error;
      });
      canvasImageCache.set(source, pending);
      return pending;
    };

    const drawFallbackMaterial = (context, item, x, y, displaySize) => {
      context.save();
      context.translate(x, y);
      context.shadowColor = 'rgba(16, 38, 63, 0.24)';
      context.shadowBlur = Math.max(3, displaySize * 0.12);
      context.shadowOffsetY = Math.max(2, displaySize * 0.06);
      const colorMap = {
        rose_quartz: '#dca3ac', amethyst: '#735287', clear_quartz: '#dce5e7', black_obsidian: '#171b21',
        tigers_eye: '#9a6425', citrine: '#d9a846', moonstone: '#cbd7da', lapis_lazuli: '#304f8a',
        gold: '#c69a40', silver: '#adb5ba', pearl: '#eee8e2', hammered: '#bd8a2f'
      };
      const color = colorMap[item.key] || colorMap[item.visual] || colorForMaterialName(item.name);
      const radius = displaySize / 2;
      if (item.kind === 'charm') {
        const glyphs = { sun: '☼', moon: '☾', star: '✦', eye: '◉' };
        context.beginPath();
        context.arc(0, 0, radius * 0.84, 0, Math.PI * 2);
        const metal = context.createLinearGradient(-radius, -radius, radius, radius);
        metal.addColorStop(0, '#fff0ac');
        metal.addColorStop(0.55, color);
        metal.addColorStop(1, '#76501a');
        context.fillStyle = metal;
        context.fill();
        context.shadowColor = 'transparent';
        context.fillStyle = '#4d3210';
        context.font = `${Math.round(displaySize * 0.48)}px Georgia, serif`;
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(glyphs[item.visual] || '✦', 0, 2);
      } else {
        const sphere = context.createRadialGradient(-radius * 0.32, -radius * 0.36, radius * 0.05, 0, 0, radius);
        sphere.addColorStop(0, '#fff');
        sphere.addColorStop(0.18, color);
        sphere.addColorStop(0.72, color);
        sphere.addColorStop(1, '#172434');
        context.beginPath();
        context.arc(0, 0, item.kind === 'spacer' ? radius * 0.76 : radius * 0.94, 0, Math.PI * 2);
        context.fillStyle = sphere;
        context.fill();
        if (item.kind === 'spacer') {
          context.shadowColor = 'transparent';
          context.globalCompositeOperation = 'destination-out';
          context.beginPath();
          context.arc(0, 0, radius * 0.25, 0, Math.PI * 2);
          context.fill();
        }
      }
      context.restore();
    };

    const ellipseArcTable = (radiusX, radiusY) => {
      const start = -Math.PI / 2;
      const steps = 384;
      const lengths = [0];
      let previousX = radiusX * Math.cos(start);
      let previousY = radiusY * Math.sin(start);
      for (let step = 1; step <= steps; step++) {
        const angle = start + step / steps * Math.PI * 2;
        const x = radiusX * Math.cos(angle);
        const y = radiusY * Math.sin(angle);
        lengths.push(lengths[step - 1] + Math.hypot(x - previousX, y - previousY));
        previousX = x;
        previousY = y;
      }
      const circumference = lengths[steps];
      return {
        circumference,
        angleAt(fraction) {
          const target = ((fraction % 1) + 1) % 1 * circumference;
          let low = 0;
          let high = steps;
          while (high - low > 1) {
            const middle = (low + high) >> 1;
            if (lengths[middle] < target) low = middle;
            else high = middle;
          }
          const segment = lengths[high] - lengths[low];
          const offset = segment > 0 ? (target - lengths[low]) / segment : 0;
          return start + (low + offset) / steps * Math.PI * 2;
        }
      };
    };

    const drawBracelet = async (context, {
      centerX, centerY, radiusX, radiusY, mmToPx, rotation = 0,
      items = cloneItems(state.items), cordVisual = state.cord.visual, designRotation = state.rotationRad,
      visibleFrontArc = false, usePhotos = true
    }) => {
      const layout = orbitLayout(items);
      const arc = ellipseArcTable(radiusX, radiusY);
      context.save();
      context.beginPath();
      context.ellipse(
        centerX, centerY, radiusX, radiusY, rotation,
        visibleFrontArc ? -Math.PI / 2 : 0,
        visibleFrontArc ? Math.PI / 2 : Math.PI * 2
      );
      context.lineWidth = Math.max(2, mmToPx * 0.48);
      context.lineCap = 'round';
      context.strokeStyle = cordVisual === 'gold' ? '#b68a38' : cordVisual === 'black' ? '#17212a' : 'rgba(88, 105, 111, 0.72)';
      context.stroke();
      context.restore();

      const slots = ringSlots(items, layout);
      const rotationCos = Math.cos(rotation);
      const rotationSin = Math.sin(rotation);
      for (const [index, item] of items.entries()) {
        const angle = arc.angleAt(slots[index].mm / layout.circumferenceMm + designRotation / (Math.PI * 2));
        // The back half is behind the photographed wrist. Drawing it over the
        // photo creates a second-looking loop that floats off the arm.
        if (visibleFrontArc && Math.cos(angle) < 0) continue;
        const factor = item.kind === 'charm' ? charmScale : item.kind === 'bead' ? 0.98 : 1;
        const displaySize = item.size * mmToPx * factor;
        const aspectRatio = Math.min(2.25, Math.max(0.44, (number(item.height) || item.size) / (number(item.width) || item.size)));
        // Charms use their real width and height, as on the stage, so the picture matches what the customer built.
        const displayWidth = item.kind === 'charm' ? (number(item.width) || item.size) * mmToPx * factor : (aspectRatio > 1.15 ? displaySize / Math.sqrt(aspectRatio) : displaySize);
        const displayHeight = item.kind === 'charm' ? (number(item.height) || item.size) * mmToPx * factor : displayWidth * aspectRatio;
        // Same hang as the stage: the loop sits on the bead rim and the body hangs outside it.
        const outward = item.kind === 'charm'
          ? (layout.beadRimMm || 0) * mmToPx + displayHeight / 2 + 1.2 * mmToPx
          : 0;
        const orbitX = Math.cos(angle) * (radiusX + outward);
        const orbitY = Math.sin(angle) * (radiusY + outward);
        const x = centerX + orbitX * rotationCos - orbitY * rotationSin;
        const y = centerY + orbitX * rotationSin + orbitY * rotationCos;
        const renderImage = item.transparentImage || item.image;
        if (renderImage) {
          try {
            if (!usePhotos) throw new Error('Photos are skipped for the quick preview.');
            const image = await loadCanvasImage(renderImage);
            context.save();
            context.shadowColor = 'rgba(16, 38, 63, 0.22)';
            context.shadowBlur = Math.max(3, displaySize * 0.12);
            context.shadowOffsetY = Math.max(2, displaySize * 0.06);
            context.save();
            context.translate(x, y);
            if (item.kind === 'charm') context.rotate(charmSwing * Math.sin(angle));
            context.drawImage(image, -displayWidth / 2, -displayHeight / 2, displayWidth, displayHeight);
            context.restore();
            context.restore();
          } catch {
            drawFallbackMaterial(context, item, x, y, displaySize);
          }
        } else {
          drawFallbackMaterial(context, item, x, y, displaySize);
        }
      }
    };

    const renderProductCanvas = async (usePhotos = true) => {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = 900;
      exportCanvas.height = 900;
      const context = exportCanvas.getContext('2d');
      const backdrop = context.createRadialGradient(450, 390, 40, 450, 450, 620);
      backdrop.addColorStop(0, '#fffdf8');
      backdrop.addColorStop(0.65, '#f4efe7');
      backdrop.addColorStop(1, '#e9e2d8');
      context.fillStyle = backdrop;
      context.fillRect(0, 0, 900, 900);
      const current = orbitLayout();
      const largestItem = state.items.reduce((largest, item) => Math.max(largest, item.size), 10);
      const physicalOuterDiameter = Math.max(62, (current.circumferenceMm / Math.PI) + Math.max(12, largestItem * 1.18, pendantOuterMm(state.items)));
      const mmToPx = Math.min(10.4, (900 * 0.76) / physicalOuterDiameter);
      const radius = (current.circumferenceMm / (Math.PI * 2)) * mmToPx;
      if (usePhotos) {
        await Promise.allSettled(state.items.map((item) => {
          const source = item.transparentImage || item.image;
          return source ? loadCanvasImage(source) : null;
        }));
      }
      await drawBracelet(context, { centerX: 450, centerY: 450, radiusX: radius, radiusY: radius, mmToPx, usePhotos });
      return exportCanvas;
    };

    const renderWristPreview = () => {
      if (!wristHost || root.dataset.photoApproved !== 'true') {
        renderWristFallback();
        return Promise.resolve();
      }
      const revision = ++visibleWristRenderRevision, id = `${activeWristTone}-${activeWristView}`;
      wristFallback && (wristFallback.hidden = true);
      wristHost.hidden = false;
      wristHost.dataset.state = 'loading';
      root.dataset.wristState = 'loading';
      threeNote.textContent = 'Loading wrist preview…';
      wristRenderChain = wristRenderChain.catch(() => {}).then(async () => {
        if (revision !== visibleWristRenderRevision || !dialog.open || !root.isConnected) return;
        try {
          const [[THREE, module], photos] = await Promise.all([loadThreeModules(), loadWristPhotos()]);
          if (revision !== visibleWristRenderRevision || !dialog.open || !root.isConnected) return;
          const photo = photos.find(entry => entry.id === id);
          wristThreeView ||= module.createBraceletView(THREE, wristHost, { interactive:false, animated:false, reducedMotion:true });
          await wristThreeView.setWrist(photo);
          if (revision !== visibleWristRenderRevision) return;
          await wristThreeView.update(threeInput());
          wristThreeView.setWristFraming(activeWristFraming);
          if (revision !== visibleWristRenderRevision) return;
          wristHost.dataset.ready = id; root.dataset.wristState = 'ready';
          threeNote.textContent = 'Illustrative try-on · Check the fit guide for your size';
        } catch (error) {
          if (revision !== visibleWristRenderRevision) return;
          renderWristFallback(error);
        }
      });
      return wristRenderChain;
    };

    const exportImageLimitBytes = 1_400_000;
    const encodedImageBytes = (dataUrl) => {
      const prefix = /^data:image\/(?:webp|png|jpeg);base64,/.exec(dataUrl)?.[0];
      if (!prefix) return Infinity;
      const length = dataUrl.length - prefix.length;
      const padding = dataUrl.endsWith('==') ? 2 : dataUrl.endsWith('=') ? 1 : 0;
      return Math.floor(length * 3 / 4) - padding;
    };
    const encodeCanvas = (canvas) => {
      let current = canvas;
      try {
        for (let attempt = 0; attempt < 5; attempt++) {
          let encoded = '';
          try {
            encoded = current.toDataURL('image/webp', 0.88);
          } catch { encoded = ''; }
          if (!/^data:image\/webp;base64,/.test(encoded)) {
            // Safari (iPhone) cannot encode WebP and returns PNG, about ten times larger.
            // Every canvas here is opaque (white or gradient backdrop), so JPEG is safe.
            try {
              encoded = current.toDataURL('image/jpeg', 0.86);
            } catch { encoded = ''; }
            if (!/^data:image\/jpeg;base64,/.test(encoded)) {
              try {
                encoded = current.toDataURL('image/png');
              } catch { encoded = ''; }
            }
          }
          if (encodedImageBytes(encoded) <= exportImageLimitBytes) return encoded;

          const width = Math.max(400, Math.floor(current.width * 0.75));
          const height = Math.max(260, Math.floor(current.height * 0.75));
          if (width >= current.width || height >= current.height) break;
          const resized = document.createElement('canvas');
          resized.width = width;
          resized.height = height;
          const context = resized.getContext('2d');
          if (!context) break;
          context.drawImage(current, 0, 0, width, height);
          current = resized;
        }
      } catch {
        throw new Error(messages.commerceError);
      }
      throw new Error(messages.commerceError);
    };

    const exportDesignMedia = async () => {
      let host, view;
      try {
        const [THREE, module] = await loadThreeModules();
        host = document.createElement('div');
        host.style.cssText = 'position:fixed;left:-10000px;top:0;width:640px;height:426px;pointer-events:none;';
        document.body.append(host);
        view = module.createBraceletView(THREE, host, { interactive:false, animated:false, reducedMotion:true });
        await view.update(threeInput());
        const productImage = encodeCanvas(view.captureCanvas({ width:1200, height:1200 }));
        const tryOnImages = [];
        if (root.dataset.photoApproved === 'true') {
          const photos = await loadWristPhotos();
          for (const angle of ['top','angle','side']) {
            await view.setWrist(photos.find(photo => photo.id === `${activeWristTone}-${angle}`));
            await view.update(threeInput());
            tryOnImages.push(encodeCanvas(view.captureCanvas({ width:1200, height:800 })));
          }
        } else for (let angle = 0; angle < 3; angle++) tryOnImages.push(encodeCanvas(view.captureCanvas({ width:1200, height:1200, angle })));
        return { productImage, tryOnImages };
      } catch {
        // WebGL remains optional: never send an old or unrelated wrist photograph.
        const productCanvas = await renderProductCanvas();
        const productImage = encodeCanvas(productCanvas);
        return { productImage, tryOnImages:[productImage, productImage, productImage] };
      } finally { view?.dispose(); host?.remove(); }
    };

    const openReviewDialog = () => {
      persistDraft();
      if (measureDialog?.open) measureDialog.close();
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      window.requestAnimationFrame(()=>{renderPreview();root.querySelector('.mel-designer__review-content').scrollTop=0;previewList.scrollTop=0;});
      void openThreePreview();
    };
    let readySheet = null;
    const showDesignReady = () => {
      persistDraft();
      const current = totals();
      if (!readySheet) {
        readySheet = document.createElement('div');
        readySheet.className = 'mel-designer__ready';
        readySheet.hidden = true;
        readySheet.innerHTML = '<div class="mel-designer__ready-card" role="dialog" aria-modal="true" aria-labelledby="mel-ready-title">'
          + '<p class="mel-designer__ready-eyebrow">Design ready</p>'
          + '<h3 id="mel-ready-title">Your design is complete</h3>'
          + '<p class="mel-designer__ready-sub">Check the details, then continue to review and checkout.</p>'
          + '<div class="mel-designer__ready-image"><img alt="" data-ready-image></div>'
          + '<dl class="mel-designer__ready-stats">'
          + '<div><dt>Total</dt><dd data-ready-total></dd></div>'
          + '<div><dt>Ring length</dt><dd data-ready-length></dd></div>'
          + '<div><dt>Beads</dt><dd data-ready-beads></dd></div>'
          + '</dl>'
          + '<div class="mel-designer__ready-actions">'
          + '<button type="button" data-ready-back>Keep editing</button>'
          + '<button type="button" data-ready-draft>Save draft</button>'
          + '<button type="button" class="mel-designer__ready-next" data-ready-next>Review &amp; checkout →</button>'
          + '</div></div>';
        root.append(readySheet);
        readySheet.addEventListener('click', (event) => {
          if (event.target === readySheet || event.target.closest('[data-ready-back]')) { readySheet.hidden = true; return; }
          if (event.target.closest('[data-ready-draft]')) { saveDraft(event.target.closest('[data-ready-draft]')); return; }
          if (event.target.closest('[data-ready-next]')) { readySheet.hidden = true; openReviewDialog(); }
        });
      }
      readySheet.querySelector('[data-ready-total]').textContent = currency.format(current.price);
      readySheet.querySelector('[data-ready-length]').textContent = current.usedMm > 0 ? `${(current.usedMm / 10).toFixed(1)} cm` : '—';
      readySheet.querySelector('[data-ready-beads]').textContent = String(state.items.length);
      const readyImage = readySheet.querySelector('[data-ready-image]');
      readySheet.hidden = false;
      // Show the drawn bracelet at once, then swap in the same photo picture the
      // order uses as soon as the bead photos arrive. A blank sheet is never shown.
      let photoReady = false;
      const paintReady = (canvas) => {
        try { readyImage.src = canvas.toDataURL('image/png'); } catch { readyImage.removeAttribute('src'); }
      };
      void renderProductCanvas(false).then((canvas) => { if (!photoReady) paintReady(canvas); }).catch(() => {});
      void renderProductCanvas(true).then((canvas) => { photoReady = true; paintReady(canvas); }).catch(() => { photoReady = true; });
    };

    const renderPreview = () => {
      requestAnimationFrame(reservePurchaseSpace);
      renderOrbit(previewCanvas, previewLayer, false);
      const current = totals();
      previewPrice.textContent = currency.format(current.price);
      if (previewName) previewName.textContent = state.designName || designNameInput?.defaultValue || 'Untitled design';
      if (reviewDesignNameInput && document.activeElement !== reviewDesignNameInput) {
        const fallback = designNameInput?.defaultValue || 'Untitled design';
        reviewDesignNameInput.value = state.designName === fallback ? '' : state.designName;
      }
      previewMeta.textContent = fitGuidance(current);
      previewMeta.dataset.designLengthMm = String(current.usedMm);
      previewMeta.dataset.beadDiameterMm = String(root.dataset.fitBeadDiameterMm || 8);
      previewMeta.dataset.beadCount = String(root.dataset.fitBeadCount || 0);
      previewMeta.parentElement.classList.toggle('is-fit-warning', state.items.length > 0 && !['snug', 'good', 'loose'].includes(current.fitStatus));
      if (fitCheckoutNote) fitCheckoutNote.hidden = true;
      const grouped = new Map();
      state.items.forEach((item) => {
        const groupKey = `${item.key || item.name}|${item.size || ''}`;
        const group = grouped.get(groupKey) || { name:item.name, size:item.size, price:number(item.price), count:0, cord:false, thumb:item.thumbnail || item.image || '' };
        group.count += 1;
        grouped.set(groupKey, group);
      });
      // The cord is a real line item even when included at $0, so the visible
      // list is the single source of truth for the total shown above it.
      const rows = [...grouped.values(), {
        name: state.cord.name || 'Cord',
        size: null,
        price: number(state.cord.price),
        count: 1,
        cord: true
      }];
      previewList.replaceChildren();
      const extraCount = Math.max(0, rows.length - 6);
      rows.forEach((group, index) => {
        const row = document.createElement('li');
        if (group.thumb) {
          const thumb = document.createElement('img');
          thumb.className = 'mel-designer__review-thumb';
          thumb.src = group.thumb;
          thumb.alt = '';
          thumb.loading = 'lazy';
          thumb.decoding = 'async';
          row.append(thumb);
        }
        const label = document.createElement('span');
        const value = document.createElement('strong');
        label.textContent = group.cord
          ? `1 × ${group.name}`
          : `${group.count} × ${group.name}${group.size ? ` (${group.size} mm)` : ''}`;
        value.textContent = currency.format(group.price * group.count);
        row.append(label, value);
        if (index >= 6) {
          row.dataset.previewExtra = 'true';
          row.hidden = !reviewListExpanded;
        }
        previewList.append(row);
      });
      const beadGroups = [...grouped.values()].filter((group) => !group.cord);
      const mainGroup = beadGroups.reduce((best, group) => (!best || group.count > best.count ? group : best), null);
      const setMetric = (name, text) => root.querySelectorAll(`[data-metric-${name}]`).forEach((node) => { node.textContent = text; });
      setMetric('length', current.usedMm > 0 ? `${(current.usedMm / 10).toFixed(1)} cm` : '—');
      setMetric('beads', String(state.items.length));
      setMetric('material', mainGroup ? mainGroup.name : '—');
      setMetric('total', currency.format(current.price));
      const controls=root.querySelector('[data-review-list-controls]');
      controls.replaceChildren();
      const mobileReviewSummary = root.querySelector('[data-preview-mobile-summary]');
      if (mobileReviewSummary) {
        mobileReviewSummary.textContent = state.items.length
          ? `${extraCount > 0 ? `+${extraCount} more · ` : ''}Total ${currency.format(current.price)}`
          : '';
        mobileReviewSummary.hidden = !state.items.length;
      }
      if(extraCount>0) {
        const moreButton=document.createElement('button');moreButton.type='button';moreButton.dataset.previewMore='';
        moreButton.className='mel-designer__review-more-button';moreButton.setAttribute('aria-expanded',String(reviewListExpanded));
        moreButton.textContent=reviewListExpanded?'Show less':`+${extraCount} more`;
        moreButton.addEventListener('click',()=>{reviewListExpanded=!reviewListExpanded;renderPreview();root.querySelector('[data-preview-more]')?.focus({preventScroll:true});});
        controls.append(moreButton);
      }
      const totalRow=document.createElement('div');totalRow.dataset.previewTotal='true';
      const totalLabel=document.createElement('span'),totalValue=document.createElement('strong');
      totalLabel.textContent='Total';totalValue.textContent=currency.format(current.price);totalRow.append(totalLabel,totalValue);controls.append(totalRow);
      // Keep the legacy node for integrations, but do not render a second
      // price box beneath the unified material list.
      if (previewPriceDetail) {
        previewPriceDetail.replaceChildren();
        previewPriceDetail.hidden = true;
        previewPriceDetail.setAttribute('aria-hidden', 'true');
      }
      const wristView = previewViewButtons.find((button) => button.dataset.previewView === 'wrist' && button.getAttribute('aria-selected') === 'true');
      if (wristView) void renderWristPreview();
    };

    const miniFallback = root.querySelector('[data-mini-fallback]');
    const miniLayer = root.querySelector('[data-mini-layer]');
    const miniHost = root.querySelector('[data-mini-three]');
    const miniLabel = root.querySelector('[data-mini-label]');
    const threeHost = root.querySelector('[data-three-host]');
    const threeNote = root.querySelector('[data-three-note]');
    const threeTools = root.querySelector('[data-three-tools]');
    const wristTab = previewViewButtons.find(button => button.dataset.previewView === 'wrist');
    const designTab = previewViewButtons.find(button => button.dataset.previewView === 'design');
    const miniPreviewButton = root.querySelector('[data-mini-preview]');
    const setDesignPreviewLabel = (fallback) => {
      if (designTab) designTab.textContent = fallback ? '2D design' : '3D';
      miniPreviewButton?.setAttribute('aria-label', fallback ? 'Open 2D bracelet preview' : 'Open 3D bracelet preview');
    };
    if (wristTab) {
      // The tab remains usable when photographs or WebGL are unavailable. The
      // selected panel then owns a deterministic 2D design fallback instead
      // of claiming that the feature is coming soon while showing another view.
      wristTab.disabled = false;
      wristTab.removeAttribute('aria-disabled');
      wristTab.removeAttribute('title');
      wristComingNote && (wristComingNote.hidden = true);
    }
    let fullThreeView, miniThreeView;
    dialog?.addEventListener('wheel', (event) => {
      if (!dialog.open || !event.target.closest('[data-preview-canvas], [data-three-host], [data-wrist-three]')) return;
      event.preventDefault();
    }, { passive: false });
    const warmedPhotos = new Map();
    let miniDesignSignature = '';
    const warmCurrentDesignImages = ({ full = false } = {}) => {
      const currentKeys = new Set(state.items.map((item) => item.key));
      const priorityMaterials = catalogItems.filter((material) => currentKeys.has(material.key));
      const firstScreenMaterials = catalogItems.filter((material) => material.kind === 'bead').slice(0, 8);
      const savedDesignImages = state.items.map((item) => ({ image: item.transparentThumbnail || item.transparentImage || item.image, thumbnail: item.transparentImage || item.thumbnail }));
      const sources = full
        ? [...savedDesignImages, ...priorityMaterials]
        : [
            ...savedDesignImages.map((item) => ({ image: item.thumbnail || catalogThumbnailUrl(item.image, 112), thumbnail: '', priority: true })),
            ...priorityMaterials.map((material) => ({ image: material.transparentThumbnail || catalogThumbnailUrl(material.thumbnail || material.image, 112), thumbnail: '' })),
            ...firstScreenMaterials.map((material) => ({ image: catalogThumbnailUrl(material.thumbnail || material.image, 112), thumbnail: '' }))
          ];
      sources.forEach((material) => {
        const url = material?.image || material?.thumbnail;
        if (!url || warmedPhotos.has(url)) return;
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        // Restored designs are the first visual promise on a return visit.
        // Give their thumbnail warm-up the same network priority as the
        // visible canvas images so a throttled cold load does not wait behind
        // the catalog response.
        image.fetchPriority = full || material.priority ? 'high' : 'low';
        image.src = url;
        warmedPhotos.set(url, image);
      });
      if (!root.dataset.priorityImageWarmMs) root.dataset.priorityImageWarmMs = String(Math.round(performance.now()));
    };
    const threeInput = () => ({ items: cloneItems(state.items), cord: { ...state.cord }, photos: warmedPhotos, wristMm:state.size === customSize ? state.customWristMm : (currentPreset().wristMin + currentPreset().wristMax) / 2, rotationRad:state.rotationRad, strandMm:currentPreset().targetMm });
    const threeFallback = (error) => {
      if (!root.isConnected || !root.ownerDocument.defaultView?.document) return;
      threeHost.hidden = true; threeTools.hidden = true; previewCanvas.hidden = false;
      miniHost.hidden = true; miniFallback.hidden = false;
      root.dataset.threeState = 'fallback';
      setDesignPreviewLabel(true);
      miniLabel && (miniLabel.textContent = '2D design ↗');
      if (error instanceof Error) root.dataset.threeError = error.message.slice(0, 180);
      if (wristTab) { wristTab.disabled = false; wristTab.removeAttribute('aria-disabled'); }
      visibleWristRenderRevision++;
      wristThreeView?.setActive(false);
      fullThreeView?.setActive(false);
      miniThreeView?.setActive(false);
      const wristSelected = previewViewButtons.some(button => button.dataset.previewView === 'wrist' && button.getAttribute('aria-selected') === 'true');
      if (wristSelected) {
        renderWristFallback(error);
      } else {
        previewViewButtons.forEach(button => button.setAttribute('aria-selected', String(button.dataset.previewView === 'design')));
        previewPanels.forEach(panel => { panel.hidden = panel.dataset.previewPanel !== 'design'; });
        wristComingNote && (wristComingNote.hidden = true);
        threeNote.textContent = threeFallbackMessage;
        renderPreview();
      }
    };
    const updatePreviewThumbnail = () => {
      if (!miniFallback || !miniLayer) return;
      if (catalogReady) state.items.forEach(item => {
        const url = catalogThumbnailUrl(item.thumbnail || item.image, 112);
        if (!url || warmedPhotos.has(url)) return;
        const photo = new Image(); photo.crossOrigin = 'anonymous'; photo.src = url;
        warmedPhotos.set(url, photo);
      });
      const signature = JSON.stringify([state.items.map(item => [item.id, item.key]), state.cord.key]);
      if (miniThreeView && signature === miniDesignSignature) return;
      miniDesignSignature = signature;
      if (!miniThreeView) {
        renderOrbit(miniFallback, miniLayer, false);
        root.querySelector('[data-mini-cord]').hidden = !state.items.length;
        root.dataset.miniUpdatedAt = String(performance.now());
      } else {
        const started = performance.now();
        void miniThreeView.update(threeInput()).then(done => {
          if (done) { root.dataset.miniUpdatedAt = String(performance.now()); root.dataset.miniUpdateMs = String(performance.now() - started); }
        }).catch(error => { miniDesignSignature = ''; threeFallback(error); });
        if (dialog.open) void fullThreeView.update(threeInput()).catch(threeFallback);
      }
    };
    const openThreePreview = async () => {
      previewViewButtons.forEach(button => button.setAttribute('aria-selected', String(button.dataset.previewView === 'design')));
      previewPanels.forEach(panel => { panel.hidden = panel.dataset.previewPanel !== 'design'; });
      setDesignPreviewLabel(false);
      const started = performance.now();
      root.dataset.threeOpenedAt = String(started);
      root.dataset.threeState = 'loading';
      miniLabel && (miniLabel.textContent = '3D preview ↗');
      threeNote.textContent = 'Loading 3D preview…';
      try {
        const [THREE, module] = await loadThreeModules();
        warmCurrentDesignImages({ full: true });
        threeHost.hidden = false; miniHost.hidden = false;
        fullThreeView ||= module.createBraceletView(THREE, threeHost, { reducedMotion });
        miniThreeView ||= module.createBraceletView(THREE, miniHost, { interactive: false, animated: false, reducedMotion });
        fullThreeView.setActive(dialog.open);
        await Promise.all([fullThreeView.update(threeInput()), miniThreeView.update(threeInput())]);
        if (!dialog.open) fullThreeView.setActive(false);
        previewCanvas.hidden = true; miniFallback.hidden = true; threeTools.hidden = false;
        root.dataset.threeState = 'ready';
        setDesignPreviewLabel(false);
        miniLabel && (miniLabel.textContent = '3D preview ↗');
        const wristSelected = previewViewButtons.some(button => button.dataset.previewView === 'wrist' && button.getAttribute('aria-selected') === 'true');
        if (wristSelected) {
          fullThreeView.setActive(false);
          threeHost.hidden = true;
          threeTools.hidden = true;
          if (root.dataset.wristState === 'fallback') renderWristFallback();
          return;
        }
        root.dataset.threeFirstFrameMs = String(performance.now() - started);
        threeNote.textContent = threeInteractionNote();
        root.dataset.miniUpdatedAt = String(performance.now());
      } catch (error) { threeFallback(error); }
    };
    dialog.addEventListener('close', () => {
      fullThreeView?.setActive(false);
      wristThreeView?.setActive(false);
      visibleWristRenderRevision++;
      root.dataset.editorView = 'edit';
      editorViewButtons.forEach((button, index) => {
        button.setAttribute('aria-selected', String(index === 0));
        button.tabIndex = index === 0 ? 0 : -1;
      });
    });
    root.addEventListener('mel-3d-fallback', threeFallback);
    root.querySelectorAll('[data-three-action]').forEach(button => button.addEventListener('click', () => {
      const action = button.dataset.threeAction;
      if (action === 'reset') fullThreeView?.reset();
      if (action === 'zoom-in') fullThreeView?.zoomBy(1.15);
      if (action === 'zoom-out') fullThreeView?.zoomBy(1 / 1.15);
      if (action === 'motion') { const pressed = button.getAttribute('aria-pressed') !== 'true'; button.setAttribute('aria-pressed', String(pressed)); button.textContent = pressed ? 'Pause' : 'Rotate'; fullThreeView?.setSpinning(pressed); }
    }));

    editorViewButtons.forEach((viewButton, index) => {
      viewButton.tabIndex = index === 0 ? 0 : -1;
      viewButton.addEventListener('click', () => {
        const view = viewButton.dataset.editorView || 'edit';
        editorViewButtons.forEach((button) => {
          const active = button === viewButton;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        root.dataset.editorView = view;
        if (view === 'edit') {
          if (dialog?.open) dialog.close();
          announce('Edit view');
          return;
        }
        if (!state.items.length) {
          announce('Choose a bead to preview.');
          editorViewButtons[0]?.focus({ preventScroll: true });
          editorViewButtons.forEach((button) => button.setAttribute('aria-selected', String(button === editorViewButtons[0])));
          root.dataset.editorView = 'edit';
          return;
        }
        persistDraft();
        if (measureDialog?.open) measureDialog.close();
        if (!dialog.open) dialog.showModal();
        renderPreview();
        root.querySelector('.mel-designer__review-content').scrollTop=0;previewList.scrollTop=0;
        if (view === '3d') {
          previewViewButtons.find((button) => button.dataset.previewView === 'design')?.click();
          void openThreePreview();
        } else {
          const wristButton = previewViewButtons.find((button) => button.dataset.previewView === 'wrist');
          wristButton?.click();
        }
      });
      viewButton.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const target = (index + (event.key === 'ArrowLeft' ? -1 : 1) + editorViewButtons.length) % editorViewButtons.length;
        editorViewButtons[target].focus();
        editorViewButtons[target].click();
      });
    });

    const persistDraft = () => {
      try {
        const json = JSON.stringify(snapshot());
        window.localStorage.setItem(storageKey, json);
        lastDraftJSON = json;
        root.dataset.draftSavedAt = String(performance.now());
        if (savedStatus) savedStatus.textContent = messages.saved;
        return true;
      } catch {
        announce(messages.draftStorageError);
        return false;
      }
    };

    const scheduleDraft = () => {
      if (!draftInitialized || JSON.stringify(snapshot()) === lastDraftJSON) return;
      window.clearTimeout(draftTimer);
      // The small local snapshot must not wait behind a paint or an image load.
      persistDraft();
    };
    window.addEventListener('pagehide', () => {
      window.clearTimeout(draftTimer);
      if (draftInitialized && JSON.stringify(snapshot()) !== lastDraftJSON) persistDraft();
    });

    // Visible confirmation. A toast sits inside the open dialog so the review
    // page cannot cover it; the designer root is used otherwise.
    const flash = (message) => {
      const host = root.querySelector('dialog[open]') || root;
      const toast = document.createElement('div');
      toast.className = 'mel-designer__flash' + (host === root ? '' : ' is-in-dialog');
      toast.setAttribute('role', 'status');
      toast.textContent = message;
      host.append(toast);
      window.setTimeout(() => toast.remove(), 2800);
    };

    const saveDraft = (trigger) => {
      if (!persistDraft()) return false;
      const savedAt = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
      if (savedStatus) savedStatus.textContent = `Draft saved at ${savedAt}`;
      announce(messages.saved);
      flash('Draft saved on this device. Reopen this page and your design is still here.');
      const buttons = new Set(root.querySelectorAll('[data-designer-action="save-dialog"], [data-ready-draft]'));
      if (trigger) buttons.add(trigger);
      buttons.forEach((button) => {
        if (!button.dataset.label) button.dataset.label = button.textContent.trim();
        button.textContent = 'Saved ✓';
        button.dataset.saved = 'true';
        window.setTimeout(() => { button.textContent = button.dataset.label; delete button.dataset.saved; }, 1800);
      });
      return true;
    };

    const shareDesign = async () => {
      const url = new URL(window.location.href);
      url.hash = `design=${shareEncode(JSON.stringify(sharePayload()))}`;
      const shareUrl = url.toString();
      const name = state.designName || 'My bracelet';
      const report = (message) => {
        if (shareStatus) { shareStatus.hidden = false; shareStatus.textContent = message; }
        announce(message);
        flash(message);
      };
      if (shareUrl.length > 2000) { report('This design is too large to share as a link.'); return; }
      history.replaceState?.(null, '', shareUrl);
      root.dataset.shareLinkLength = String(shareUrl.length);
      // Phones open the system share sheet; desktops fall through to copying the link.
      if (navigator.share) {
        try {
          await navigator.share({ title: name, text: `${name}, designed in the MEL bracelet designer`, url: shareUrl });
          report('Design shared.');
          return;
        } catch (error) {
          if (error && error.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        report('Link copied. Paste it anywhere to open this design.');
      } catch {
        report('Could not copy automatically. Copy the link from the address bar.');
      }
    };

    const setCommerceStatus = (message, isError = false) => {
      if (!commerceStatus) return;
      commerceStatus.hidden = false;
      commerceStatus.textContent = message;
      commerceStatus.classList.toggle('is-error', isError);
    };

    const addDesignToCart = async (product) => {
      if (!window.MelDesignerCart) throw new Error(messages.serviceUnavailable);
      return window.MelDesignerCart.add({
        requestId: state.requestId,
        variantId: product.variantId,
        designId: product.designId || product.lineItemProperties?.['Design ID'],
        lineItemProperties: {
          ...(product.lineItemProperties || {}),
          'Design name': state.designName || 'Untitled design'
        },
        amount: product.amount,
        currencyCode: product.currencyCode || root.dataset.currencyCode
      });
    };

    const createDesignProduct = async (design) => {
      const endpoint = root.dataset.commerceEndpoint;
      if (!endpoint) throw new Error(messages.serviceUnavailable);

      const payload = JSON.stringify(design);
      // Every attempt reuses the same requestId, so a repeat can never create a second
      // product. While the first attempt is still running the server answers 409 "already
      // being created"; waiting and asking again is correct. The server allows five
      // attempts per request id per minute, so the retry pacing stays inside that.
      const attemptTimeoutMs = 25000;
      const maxAttempts = 5;
      const deadline = Date.now() + 110000;
      const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
      const slowNotice = window.setTimeout(() => {
        setCommerceStatus('Still creating your bracelet. This can take up to a minute.');
      }, 8000);
      const sendOnce = async () => {
        const controller = new AbortController();
        const timer = window.setTimeout(() => controller.abort(), attemptTimeoutMs);
        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: payload,
            signal: controller.signal
          });
          const product = await response.json().catch(() => null);
          return { response, product };
        } finally {
          window.clearTimeout(timer);
        }
      };
      try {
        for (let attempt = 1; ; attempt += 1) {
          const canRetry = attempt < maxAttempts && Date.now() < deadline;
          let result;
          try {
            result = await sendOnce();
          } catch {
            if (!canRetry) throw new Error(`${messages.commerceError} Tap Add to cart to try again.`);
            await pause(3000);
            continue;
          }
          const { response, product } = result;
          if (response.ok && product?.ok && product.variantId) return product;
          const busy = response.status === 409 && /already being created/i.test(String(product?.error || ''));
          if (busy) {
            if (!canRetry) throw new Error('Your bracelet is still being created. Tap Add to cart again in a moment.');
            await pause(6000);
            continue;
          }
          if (response.status >= 500 && canRetry) {
            await pause(3000);
            continue;
          }
          const error = new Error(product?.error || messages.commerceError);
          if (product?.code === 'PRICE_CHANGED' && response.status === 409) {
            error.code = 'PRICE_CHANGED';
            error.amount = product.amount;
            error.currencyCode = product.currencyCode;
          }
          throw error;
        }
      } finally {
        window.clearTimeout(slowNotice);
      }
    };

    let designerCartDrawerPromise;
    const ensureDesignerCartDrawer = () => {
      const existing = document.querySelector('cart-drawer');
      if (existing) return Promise.resolve(existing);
      if (designerCartDrawerPromise) return designerCartDrawerPromise;
      designerCartDrawerPromise = (async () => {
        const shopRoot = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${shopRoot}?section_id=cart-drawer`, { signal: AbortSignal.timeout(12000) });
        if (!response.ok) throw new Error('Cart display unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const drawer = html.querySelector('cart-drawer');
        if (!drawer) throw new Error('Cart display unavailable');
        document.body.appendChild(drawer);
        if (!customElements.get('cart-drawer')) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            const timeout = setTimeout(() => { script.remove(); drawer.remove(); reject(new Error('Cart display timeout')); }, 12000);
            script.src = root.dataset.cartScript;
            script.onload = () => { clearTimeout(timeout); resolve(); };
            script.onerror = () => { clearTimeout(timeout); drawer.remove(); reject(new Error('Cart display unavailable')); };
            document.head.appendChild(script);
          });
        }
        if (!customElements.get('cart-drawer')) { drawer.remove(); throw new Error('Cart display unavailable'); }
        return drawer;
      })().catch((error) => { designerCartDrawerPromise = null; throw error; });
      return designerCartDrawerPromise;
    };

    const confirmDesign = async (button, checkoutAfter = false) => {
      if (state.commerceBusy || !state.items.length) return;
      if (!fitAllowsPurchase(totals())) {
        setCommerceStatus(fitGuidance(totals()));
        return;
      }
      if (!catalogReady) {
        setCommerceStatus(catalogLoadFailed ? messages.catalogError : messages.catalogLoading, true);
        return;
      }
      if (getCatalogIssues().length) {
        setCommerceStatus(messages.catalogStale, true);
        return;
      }
      state.commerceBusy = true;
      root.setAttribute('aria-busy', 'true');
      dialog.dataset.busy = 'true';
      state.requestId ||= window.crypto?.randomUUID?.() || `mel-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
      persistDraft();
      button.dataset.idleLabel ||= button.textContent.trim();
      button.textContent = messages.checking;
      setCommerceStatus(messages.checking);
      updateActionState();
      const drawerReady = checkoutAfter ? null : ensureDesignerCartDrawer();
      drawerReady?.catch(() => {});
      const finishCart = async (cartResult) => {
        if (cartLink) {
          cartLink.href = cartResult.cartUrl;
          cartLink.hidden = false;
        }
        setCommerceStatus(messages.cartAdded);
        root.dataset.commerceDoneMs = String(performance.now());
        if (!checkoutAfter) {
          dialog.close();
          try {
            await drawerReady;
            document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
          } catch {
            setCommerceStatus('Added to your cart. Open cart to continue.');
          }
        }
        if (checkoutAfter) {
          const checkoutRoot = window.Shopify?.routes?.root || '/';
          window.location.assign(checkoutRoot + 'checkout');
        }
      };
      try {
        if (!window.MelDesignerCart) throw new Error(messages.serviceUnavailable);
        const recoveredCart = await window.MelDesignerCart.reconcilePending?.({ requestId: state.requestId });
        if (recoveredCart) {
          await finishCart(recoveredCart);
          return;
        }
        const progressSteps = [...root.querySelectorAll('[data-commerce-step]')];
        const showStage = (stage) => {
          root.querySelector('[data-commerce-progress]').hidden = false;
          root.dataset.commercePhase = stage;
          root.dataset[`commerce${stage.charAt(0).toUpperCase() + stage.slice(1)}Ms`] = String(performance.now());
          progressSteps.forEach((step) => { step.classList.toggle('is-current', step.dataset.commerceStep === stage); if (step.dataset.commerceStep === stage) step.dataset.seen = 'true'; });
        };
        showStage('images');
        button.textContent = 'Generating images…';
        setCommerceStatus('Generating your design images…');
        const media = await exportDesignMedia();
        showStage('product');
        button.textContent = messages.creating;
        setCommerceStatus(messages.creating);
        const current = totals();
        const activeCurrency = String(root.dataset.currencyCode || '').toUpperCase();
        const designPayload = {
          requestId: state.requestId,
          designName: state.designName,
          size: state.size === 'XL' ? customSize : state.size,
          wristMm: state.size === 'XL' ? 180 : (current.preset.isCustom ? current.preset.wristMin : null),
          targetMm: current.capacityMm,
          cordKey: state.cord.key,
          items: state.items.map((item) => ({ key: item.key, assetUrl: item.image || '' })),
          media,
          expectedAmountCents: Math.round(current.price * 100),
          expectedCurrencyCode: activeCurrency
        };
        let product;
        try {
          product = await createDesignProduct(designPayload);
        } catch (error) {
          if (error?.code !== 'PRICE_CHANGED') throw error;
          const revisedAmount = Number(error.amount);
          const revisedCurrency = String(error.currencyCode || '').toUpperCase();
          if (!Number.isFinite(revisedAmount) || revisedAmount < 0 || revisedCurrency !== activeCurrency) {
            throw new Error(messages.commerceError);
          }
          const accepted = window.confirm(messages.priceChanged + ' ' + currency.format(revisedAmount));
          if (!accepted) {
            setCommerceStatus(messages.priceChanged, true);
            return;
          }
          designPayload.expectedAmountCents = Math.round(revisedAmount * 100);
          product = await createDesignProduct(designPayload);
        }
        const productCurrency = String(product.currencyCode || '').toUpperCase();
        const verifiedAmount = Number(product.amount);
        if (!Number.isFinite(verifiedAmount) || verifiedAmount < 0) {
          throw new Error(messages.commerceError);
        }
        if (!productCurrency || productCurrency !== activeCurrency) {
          throw new Error('The server price currency does not match the displayed currency. Please contact support before adding this design.');
        }
        const verifiedAmountCents = Math.round(verifiedAmount * 100);
        if (verifiedAmountCents !== designPayload.expectedAmountCents) {
          if (!product.recoveredFromPriorRequest) {
            throw new Error('The product price differs from the price you accepted. No item was added. Contact support with your saved design reference.');
          }
          const acceptedPriorPrice = window.confirm(messages.priceChanged + ' ' + currency.format(verifiedAmount));
          if (!acceptedPriorPrice) {
            setCommerceStatus(messages.priceChanged, true);
            return;
          }
          designPayload.expectedAmountCents = verifiedAmountCents;
        }
        previewPrice.textContent = currency.format(verifiedAmount);
        root.dataset.createdProductId = String(product.productId || '');
        root.dataset.createdDesignId = String(product.designId || product.lineItemProperties?.['Design ID'] || '');
        showStage('cart');
        button.textContent = messages.carting;
        setCommerceStatus(messages.carting);
        const cartResult = await addDesignToCart(product);
        await finishCart(cartResult);
      } catch (error) {
        const cartUnknown = error?.code === 'CART_OUTCOME_UNKNOWN';
        const cartNeedsReview = cartUnknown || ['CART_RECOVERY_CONFLICT', 'CART_RECOVERY_UNAVAILABLE'].includes(error?.code);
        if (cartNeedsReview && cartLink) {
          cartLink.href = error?.cartUrl || (window.Shopify?.routes?.root || '/') + 'cart';
          cartLink.hidden = false;
        }
        const reference = String(error?.operationId || state.requestId || '');
        const message = cartUnknown
          ? messages.cartUnknown.replace('__REFERENCE__', reference)
          : cartNeedsReview
            ? messages.cartRecoveryConflict
            : error?.message && error.message !== 'Failed to fetch' ? error.message : messages.commerceError;
        persistDraft();
        setCommerceStatus(message, true);
        announce(message);
      } finally {
        state.commerceBusy = false;
        root.removeAttribute('aria-busy');
        delete dialog.dataset.busy;
        button.textContent = button.dataset.idleLabel;
        updateActionState();
      }
    };

    const filterMaterials = () => {
      const category = activeCategory();
      if (catalogReady && category !== 'cord') {
        renderCatalog(category);
        return;
      }
      const query = String(materialSearch?.value || '').trim().toLocaleLowerCase();
      const activePanel = panels.find((panel) => !panel.hidden);
      let visibleCount = 0;
      materialButtons.forEach((button) => {
        const searchable = `${button.dataset.name || ''} ${button.dataset.key || ''}`.toLocaleLowerCase();
        const isMatch = !query || searchable.includes(query);
        button.hidden = !isMatch;
        if (isMatch && button.closest('[data-category-panel]') === activePanel) visibleCount += 1;
      });
      if (materialEmpty) materialEmpty.hidden = visibleCount > 0;
    };

    bindMaterialButtons();
    materialDragMedia.addEventListener('change', () => {
      materialButtons.forEach(syncMaterialDraggable);
    });
    materialSearch?.addEventListener('input', () => {
      if (materialSearch.value.trim() && activeCategory() === 'cord') {
        tabs.find((tab) => tab.dataset.categoryTab === 'beads')?.click();
        return;
      }
      filterMaterials();
    });

    fitAdviceButton?.addEventListener('click', (event) => {
      event.preventDefault();
      const expanded = fitAdviceButton.getAttribute('aria-expanded') === 'true';
      fitAdviceButton.setAttribute('aria-expanded', String(!expanded));
      root.querySelector('[data-fit-status]')?.classList.toggle('is-expanded', !expanded);
      requestRender();
    });

    catalogRetryButtons.forEach((button) => button.addEventListener('click', () => { void loadMaterialCatalog(); }));

    catalogReconcileReplace?.addEventListener('click', () => {
      const target = catalogReconciliation.unavailable[0]?.item || getCatalogIssues()[0];
      if (!target) return;
      replacingId = target.id;
      replacingAllKey = null;
      root.classList.remove('is-replacing-all');
      root.classList.add('is-replacing');
      activeMaterialType = target.materialType || 'all';
      materialTypes?.querySelectorAll('[data-material-type]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.materialType === activeMaterialType));
      });
      const beadTab = tabs.find((tab) => tab.dataset.categoryTab === 'beads');
      beadTab?.click();
      if (materialSearch) {
        materialSearch.value = '';
        filterMaterials();
      }
      setPanelMode?.('full');
      announce('Choose a current material to replace the unavailable piece.');
    });

    catalogReconcileRemove?.addEventListener('click', () => {
      const ids = new Set(catalogReconciliation.unavailable.map(({ item }) => item.id));
      if (!ids.size) return;
      remember();
      state.items = state.items.filter((item) => !ids.has(item.id));
      if (ids.has(state.selectedId)) state.selectedId = null;
      replacingId = null;
      replacingAllKey = null;
      catalogReconciliation.unavailable = [];
      catalogReconciliation.updated = [];
      root.classList.remove('is-replacing', 'is-replacing-all');
      scheduleDraft();
      updateReconciliationBanner();
      render();
      announce(`Removed ${ids.size} unavailable ${ids.size === 1 ? 'piece' : 'pieces'}.`);
    });

    materialTypes?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-material-type]');
      if (!button || !materialTypes.contains(button)) return;
      activeMaterialType = button.dataset.materialType || 'all';
      materialTypes.querySelectorAll('[data-material-type]').forEach((candidate) => {
        candidate.setAttribute('aria-pressed', String(candidate === button));
      });
      renderCatalog('beads');
    });

    const mobileTabLayout = window.matchMedia('(max-width: 749px)');
    const syncTabOrientation = () => tabList?.setAttribute('aria-orientation', 'horizontal');
    syncTabOrientation();
    mobileTabLayout.addEventListener('change', syncTabOrientation);

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        tabs.forEach((item) => {
          const active = item === tab;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach((panel) => { panel.hidden = panel.dataset.categoryPanel !== tab.dataset.categoryTab; });
        filterMaterials();
      });
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let target = index;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = (index + 1) % tabs.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = tabs.length - 1;
        tabs[target].focus();
        tabs[target].click();
      });
    });

    previewViewButtons.forEach((viewButton, index) => {
      viewButton.addEventListener('click', () => {
        previewViewButtons.forEach((button) => {
          const active = button === viewButton;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        previewPanels.forEach((panel) => { panel.hidden = panel.dataset.previewPanel !== viewButton.dataset.previewView; });
        if (viewButton.dataset.previewView === 'design') {
          visibleWristRenderRevision++;
          wristThreeView?.setActive(false);
          wristFallback && (wristFallback.hidden = true);
          wristComingNote && (wristComingNote.hidden = true);
          setDesignPreviewLabel(root.dataset.threeState === 'fallback');
          previewCanvas.hidden = root.dataset.threeState === 'ready';
          fullThreeView?.setActive(true);
          threeHost.hidden = root.dataset.threeState !== 'ready';
          threeTools.hidden = root.dataset.threeState !== 'ready';
          threeNote.textContent = root.dataset.threeState === 'ready' ? threeInteractionNote() : threeFallbackMessage;
        }
        else {
          threeHost.hidden = true;
          threeTools.hidden = true;
          previewCanvas.hidden = true;
          fullThreeView?.setActive(false);
          wristThreeView?.setActive(true);
          wristComingNote && (wristComingNote.hidden = true);
          void renderWristPreview();
        }
      });
      viewButton.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        const target = (index + (event.key === 'ArrowLeft' ? -1 : 1) + previewViewButtons.length) % previewViewButtons.length;
        previewViewButtons[target].focus();
        previewViewButtons[target].click();
      });
    });

    wristViewButtons.forEach((viewButton, index) => {
      viewButton.tabIndex = index === 0 ? 0 : -1;
      viewButton.addEventListener('click', () => {
        activeWristView = viewButton.dataset.wristView;
        wristViewButtons.forEach((button) => {
          const active = button === viewButton;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        void renderWristPreview();
      });
      viewButton.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let target = index;
        if (event.key === 'ArrowLeft') target = (index - 1 + wristViewButtons.length) % wristViewButtons.length;
        if (event.key === 'ArrowRight') target = (index + 1) % wristViewButtons.length;
        if (event.key === 'Home') target = 0;
        if (event.key === 'End') target = wristViewButtons.length - 1;
        wristViewButtons[target].focus();
        wristViewButtons[target].click();
      });
    });

    wristToneButtons.forEach(button => button.addEventListener('click', () => {
      activeWristTone = button.dataset.wristTone;
      wristToneButtons.forEach(candidate => candidate.setAttribute('aria-pressed', String(candidate === button)));
      void renderWristPreview();
    }));
    wristFramingButton?.addEventListener('click', () => {
      activeWristFraming = activeWristFraming === 'wrist' ? 'whole' : 'wrist';
      wristFramingButton.setAttribute('aria-pressed', String(activeWristFraming === 'whole'));
      wristFramingButton.textContent = activeWristFraming === 'whole' ? 'Focus on wrist' : 'View whole hand';
      wristThreeView?.setWristFraming(activeWristFraming);
    });

    const clearDesign = () => {
      if (!state.items.length) return;
      remember();
      state.items = [];
      state.selectedId = null;
      state.zoom = 1;
      render();
      if (dialog?.open) renderPreview();
      announce('Design cleared');
      showClearToast();
    };

    const undoDesign = () => {
      const previous = state.history.pop();
      if (!previous) return;
      restoreSnapshot(previous);
      render();
      if (dialog?.open) renderPreview();
      announce('Design restored');
      hideClearToast();
    };

    clearToastUndo?.addEventListener('click', undoDesign);

    actionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const action = button.dataset.designerAction;
        if (action === 'undo') {
          undoDesign();
        }
        if (action === 'clear') {
          clearDesign();
        }
        if (action === 'inspire') inspireDesign();
        if (action === 'move-previous') moveSelected(-1);
        if (action === 'move-next') moveSelected(1);
        if (action === 'remove') removeSelected();
        if (action === 'fill-to-fit') fillToFit();
        if (action === 'replace') {
          replacingId = state.selectedId;
          replacingAllKey = null;
          root.classList.remove('is-replacing-all');
          root.classList.add('is-replacing');
          activeMaterialType = 'all';
          materialTypes?.querySelectorAll('[data-material-type]').forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate.dataset.materialType === 'all')));
          tabs.find((tab) => tab.dataset.categoryTab === 'beads')?.click();
          if (materialSearch) { materialSearch.value = ''; filterMaterials(); }
          setPanelMode?.('full');
          announce('Choose a material to replace the selected bead.');
        }
        if (action === 'replace-all') {
          const selected = state.items.find((item) => item.id === state.selectedId);
          if (!selected) { announce('Select a piece to replace.'); return; }
          replacingId = null;
          replacingAllKey = selected.key;
          root.classList.remove('is-replacing');
          root.classList.add('is-replacing-all');
          activeMaterialType = 'all';
          materialTypes?.querySelectorAll('[data-material-type]').forEach((candidate) => candidate.setAttribute('aria-pressed', String(candidate.dataset.materialType === 'all')));
          tabs.find((tab) => tab.dataset.categoryTab === 'beads')?.click();
          if (materialSearch) { materialSearch.value = ''; filterMaterials(); }
          setPanelMode?.('full');
          announce('Choose a material to replace every matching piece.');
        }
        if (action === 'repeat-pattern') repeatSelectedPattern();
        if (action === 'mirror') mirrorDesign();
        if (action === 'duplicate') {
          const index = state.items.findIndex((item) => item.id === state.selectedId);
          const button = materialButtons.find((item) => item.dataset.key === state.items[index]?.key);
          if (button && !button.disabled) addMaterial(button, index + 1);
        }
        if (action === 'custom-size') {
          if (state.size !== customSize) { remember(); state.size = customSize; render(); }
          measureDialog?.showModal(); customWristInput?.focus();
        }
        if (action === 'measure') {
          if (typeof measureDialog?.showModal === 'function' && !measureDialog.open) measureDialog.showModal();
          else measureDialog?.setAttribute('open', '');
        }
        if (action === 'zoom-out') {
          setZoom(state.zoom - 0.2);
        }
        if (action === 'zoom-in') {
          setZoom(state.zoom + 0.2);
        }
        if (action === 'fit-screen') {
          userAdjustedZoom = false;
          state.zoom = 1;
          render();
        }
        if (action === 'review') showDesignReady();
        if (action === 'save' || action === 'save-dialog') saveDraft(button);
        if (action === 'share') void shareDesign();
        if (action === 'add-to-cart') void confirmDesign(button, false);
        if (action === 'confirm') void confirmDesign(button, true);
      });
    });

    designNameInput?.addEventListener('input', () => {
      state.designName = designNameInput.value.trimStart().slice(0, 64) || designNameInput.defaultValue;
      state.requestId = '';
      if (previewName) previewName.textContent = state.designName;
      scheduleDraft();
    });

    designNameInput?.addEventListener('change', persistDraft);

    reviewDesignNameInput?.addEventListener('input', () => {
      const fallback = designNameInput?.defaultValue || 'Untitled design';
      const value = reviewDesignNameInput.value.trimStart().slice(0, 64);
      state.designName = value || fallback;
      state.requestId = '';
      if (designNameInput && document.activeElement !== designNameInput) designNameInput.value = state.designName;
      if (previewName) previewName.textContent = state.designName;
      scheduleDraft();
    });
    reviewDesignNameInput?.addEventListener('change', persistDraft);

    root.querySelectorAll('[data-starter]').forEach((button) => button.addEventListener('click', () => {
      const expression = { clear: /Clear White Crystal/, amethyst: /Amethyst/i, tiger: /Tiger.?s? Eye/i }[button.dataset.starter];
      const material = catalogItems.find((item) => item.kind === 'bead' && item.size === 8 && expression.test(item.name));
      if (material) fillToFit(material.key);
    }));
    // Phone material panel. Drag the handle up for more materials or down for a
    // bigger design; a tap jumps between the default size and full height.
    const panelLibrary = root.querySelector('.mel-designer__library');
    const panelStatus = root.querySelector('.mel-designer__status');
    const panelBody = root.querySelector('.mel-designer__body');
    const panelStops = () => {
      const inner = Math.max(0, (panelBody?.clientHeight || 0) - (panelStatus?.offsetHeight || 56));
      return { inner, less: 220, default: Math.max(220, Math.min(320, inner - 244)), full: inner };
    };
    const applyPanelHeight = (px) => {
      const stops = panelStops();
      const height = Math.round(Math.min(stops.full, Math.max(stops.less, px)));
      root.style.setProperty('--mel-panel-h', `${height}px`);
      // Below 150px the design stage is too small to read, so hide it.
      root.dataset.stageHidden = String(stops.inner - height < 150);
      return height;
    };
    const showPanelHeight = (height) => {
      const stops = panelStops();
      const applied = applyPanelHeight(height);
      const expanded = applied >= stops.full;
      root.dataset.panelMode = expanded ? 'full' : 'collapsed';
      root.dataset.panelExpanded = String(expanded);
      panelHandle?.setAttribute('aria-expanded', String(expanded));
      const label = root.querySelector('[data-panel-label]');
      if (label) label.textContent = expanded ? 'Show design' : 'More';
    };
    const setPanelMode = (mode) => {
      const normalized = ['collapsed', 'half', 'full'].includes(mode) ? mode : 'collapsed';
      if (normalized !== 'collapsed') panels.forEach((panel) => { panel.scrollTop = 0; });
      showPanelHeight(normalized === 'full' ? panelStops().full : panelStops().default);
    };
    const togglePanel = (expanded) => setPanelMode(expanded ? 'full' : 'collapsed');
    let panelDrag = null;
    let suppressPanelClickUntil = 0;
    // Keep the height where the finger lifts; only clamp it and update the mode.
    const syncPanel = () => showPanelHeight(panelLibrary ? panelLibrary.getBoundingClientRect().height : panelStops().default);
    panelHandle?.addEventListener('click', () => {
      if (performance.now() < suppressPanelClickUntil) return;
      setPanelMode(root.dataset.stageHidden === 'true' ? 'collapsed' : 'full');
    });
    panelHandle?.addEventListener('pointerdown', (event) => {
      if (!panelLibrary) return;
      panelDrag = { id: event.pointerId, startY: event.clientY, startH: panelLibrary.getBoundingClientRect().height, moved: false };
      try { panelHandle.setPointerCapture(event.pointerId); } catch { /* ignore: capture is best effort */ }
    });
    panelHandle?.addEventListener('pointermove', (event) => {
      if (!panelDrag || event.pointerId !== panelDrag.id) return;
      const delta = panelDrag.startY - event.clientY;
      if (!panelDrag.moved && Math.abs(delta) < 6) return;
      panelDrag.moved = true;
      applyPanelHeight(panelDrag.startH + delta);
    });
    const endPanelDrag = (event) => {
      if (!panelDrag || event.pointerId !== panelDrag.id) return;
      const moved = panelDrag.moved;
      panelDrag = null;
      if (!moved) return;
      suppressPanelClickUntil = performance.now() + 300;
      syncPanel();
    };
    panelHandle?.addEventListener('pointerup', endPanelDrag);
    panelHandle?.addEventListener('pointercancel', endPanelDrag);
    window.addEventListener('resize', () => {
      if (panelLibrary && root.style.getPropertyValue('--mel-panel-h')) applyPanelHeight(panelLibrary.getBoundingClientRect().height);
    });
    const updateFilterFade = () => {
      if (!materialTypes) return;
      materialTypes.dataset.fadeStart = String(materialTypes.scrollLeft > 2);
      materialTypes.dataset.fadeEnd = String(materialTypes.scrollLeft + materialTypes.clientWidth < materialTypes.scrollWidth - 2);
    };
    materialTypes?.addEventListener('scroll', updateFilterFade, { passive: true });
    if (materialTypes && 'ResizeObserver' in window) new ResizeObserver(updateFilterFade).observe(materialTypes);
    root.addEventListener('keydown', (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.target.closest('input, textarea')) {
        event.preventDefault(); actionButtons.find((button) => button.dataset.designerAction === 'undo')?.click();
      }
    });

    sizeButtons.forEach((button) => button.addEventListener('click', () => {
      if (button.dataset.size === state.size) return;
      remember();
      state.size = button.dataset.size;
      state.customAutoFit = false;
      render();
    }));

    wristUnitButtons.forEach((button) => button.addEventListener('click', () => {
      if (!wristUnits[button.dataset.wristUnit] || button.dataset.wristUnit === wristDisplayUnit) return;
      wristDisplayUnit = button.dataset.wristUnit;
      syncWristUnit(true);
    }));

    if (customWristInput) {
      let customInputStart = null;
      let customInputRemembered = false;

      customWristInput.addEventListener('focus', () => {
        customInputStart = snapshot();
        customInputRemembered = false;
        if (state.size === customSize) {
          state.customAutoFit = false;
          return;
        }
        remember(customInputStart);
        customInputRemembered = true;
        state.size = customSize;
        state.customAutoFit = false;
        announce(`${labels.custom} ${state.customWristMm} mm`);
        render();
      });

      customWristInput.addEventListener('input', () => {
        const wristMm = enteredWristMm();
        if (!Number.isFinite(wristMm) || wristMm < customWristMin || wristMm > customWristMax) return;
        if (!customInputRemembered) {
          remember(customInputStart || snapshot());
          customInputRemembered = true;
        }
        state.size = customSize;
        state.customWristMm = wristMm;
        state.customAutoFit = false;
        state.requestId = '';
        if (wristHost) delete wristHost.dataset.ready;
        render();
      });

      customWristInput.addEventListener('blur', () => {
        const enteredWrist = enteredWristMm() ?? state.customWristMm;
        state.customWristMm = Math.round(Math.min(customWristMax, Math.max(customWristMin, enteredWrist)));
        customInputStart = null;
        customInputRemembered = false;
        render();
      });
    }

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    measureDialog?.addEventListener('click', (event) => {
      if (event.target === measureDialog) measureDialog.close();
    });
    cardCalibrateButton?.addEventListener('click', () => {
      root.dataset.cardReferenceCalibrated = 'true';
      cardCalibrateButton.setAttribute('aria-pressed', 'true');
      cardCalibrateButton.textContent = 'Reference ready';
      if (cardRulerStatus) cardRulerStatus.textContent = '54.0 mm reference ready. Align the card edge with the illustrated ruler.';
      announce('54.0 millimeter reference ready.');
    });

    state.zoom = 1;
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey));
      lastDraftJSON = JSON.stringify(saved);
      if (restoreSnapshot(saved) && state.items.length) announce(messages.restored);
    } catch (error) {
      try { window.localStorage.removeItem(storageKey); } catch {}
    }

    if ('ResizeObserver' in window) {
      let lastCanvasSize = null;
      const resizeObserver = new ResizeObserver(() => {
        syncTabOrientation();
        materialButtons.forEach(syncMaterialDraggable);
        const canvasSize = { width: canvas.clientWidth, height: canvas.clientHeight };
        if (lastCanvasSize && !userAdjustedZoom && (
          Math.abs(canvasSize.width - lastCanvasSize.width) > 4 ||
          Math.abs(canvasSize.height - lastCanvasSize.height) > 4
        )) {
          state.zoom = 1;
        }
        if (state.items.length) fitOrbitZoom();
        else state.zoom = 1;
        lastCanvasSize = canvasSize;
        requestRender();
        if (dialog.open) renderPreview();
      });
      resizeObserver.observe(canvas);
    }

    bindCanvasRotation();
    lastDraftJSON = JSON.stringify(snapshot());
    draftInitialized = true;
    if (state.items.length) fitOrbitZoom();
    requestRender();
    // Start warming images from the saved draft before the live catalog
    // request resolves; the first render can therefore replace placeholders
    // without waiting for the catalog paint milestone.
    if (state.items.length) warmCurrentDesignImages();
    void loadMaterialCatalog();
  };

  const boot = () => {
    const roots = [...document.querySelectorAll('[data-mel-bracelet-designer]')];
    roots.forEach(initDesigner);
  };

  boot();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
